import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { action, computed, makeObservable, observable, override, reaction, runInAction, toJS } from 'mobx';
import moment from 'moment';

import { ABAS_DETALHES_PROCESSO } from '@/dominio/AbasDetalhesProcesso';
import { MEIO_JULGAMENTO_PROCESSO } from '@/dominio/MeioJulgamentoProcesso';
import ModoParticipacaoBusiness from '@/dominio/ModoParticipacaoBusiness';
import { RESULTADO_JULGAMENTO } from '@/dominio/ResultadoJulgamento';
import SituacaoJulgamentoProcessoBusiness from '@/dominio/SituacaoJulgamentoProcessoBusiness';
import HtmlDiff from '@/util/HtmlDiff';
import { LOCALSTORAGE_STORAGE } from '@/util/StorageUtil';

import FiltrosSalaSessao from '../components/FiltrosSalaSessao';
import { MODO_PARTICIPACAO_ADVOGADO } from '../dominio/ModoParticipacaoAdvogado';
import { SITUACAO_INSCRICAO_ADVOGADO } from '../dominio/SituacaoInscricaoAdvogado';
import { SITUACAO_PUBLICACAO_PAUTA } from '../dominio/SituacaoPublicacaoPauta';
import RepresentanteParte, { InscricaoRepresentanteParte } from '../entities/RepresentanteParte';
import ApmVars from '../util/ApmVars';
import Configuracao from '../util/Configuracao';
import ErrorUtil from '../util/ErrorUtil';
import SentryUtil from '../util/SentryUtil';
import StringUtil from '../util/StringUtil';
import { db, VersaoMinutaVoto, VersaoMinutaVotoControleAcesso } from './db';
import Store from './Store';

// eslint-disable-next-line unicorn/no-top-level-side-effects
dayjs.extend(customParseFormat);

export const SITUACAO_CARREGAMENTO_VERSAO_MINUTA = {
    CARREGADO_DO_CACHE: 'Carregado do cache',
    EM_ANDAMENTO: 'Carregando',
    FINALIZADO: 'Carregado',
    FALHOU: 'Falhou'
};

export const MODO_CARREGAMENTO_VERSOES_MINUTA = {
    TODAS: 'Todos os conteúdos',
    APENAS_VERSOES_COM_ETAG_DIFERENTES: 'Apenas os conteúdos com eTag diferentes',
    SOB_DEMANDA: 'Apenas se o conteúdo for requisitado pelo usuário'
};

const EXIBIR_VERSOES_SEM_ALTERACAO_CONTEUDO =
    Configuracao.VISUALIZADOR_VERSAO_MINUTA_VOTO__EXIBIR_VERSOES_SEM_ALTERACAO_CONTEUDO;

/**
 * Classe utilizada para guardar e manipular o estado da aplicação. Deve-se
 * criar uma para cada contexto ou entidade.
 */
export default class SalaSessaoStore extends Store {
    fixarPainelJulgamento = false;
    mensagemErroEnvioLote = [];
    abrirMensagemErroLote = false;
    quantidadeErroMovimento = 0;
    quantidadeErroCertidao = 0;
    quantidadeErroAcordao = 0;
    modalConfirmacaoEditarVoto = false;
    confirmarEdicaoVoto = true;
    campoFocoPosOrdenacao;
    painelInicioCertidaoExpandido = true;
    painelDecisaoCertidaoExpandido = true;
    painelPartesCertidaoExpandido = true;
    exibirProcessosComCertidao = false;
    modalCertidaoAberto = false;
    controleCarregamentoVersoesMinutaVoto = [];
    dialogDetalhesProcessoApregoadoOpen = false;
    dialogComplementoAnotacoesOpen = false;
    dialogConteudoComplementoAnotacoes = '';
    detalhesProcessoApregoado = {};

    constructor(props) {
        super(props);

        makeObservable(this, {
            fixarPainelJulgamento: observable,
            mensagemErroEnvioLote: observable,
            abrirMensagemErroLote: observable,
            quantidadeErroMovimento: observable,
            quantidadeErroCertidao: observable,
            quantidadeErroAcordao: observable,
            modalConfirmacaoEditarVoto: observable,
            confirmarEdicaoVoto: observable,
            campoFocoPosOrdenacao: observable,
            painelInicioCertidaoExpandido: observable,
            painelDecisaoCertidaoExpandido: observable,
            painelPartesCertidaoExpandido: observable,
            exibirProcessosComCertidao: observable,
            modalCertidaoAberto: observable,
            controleCarregamentoVersoesMinutaVoto: observable,
            dialogDetalhesProcessoApregoadoOpen: observable,
            dialogComplementoAnotacoesOpen: observable,
            dialogConteudoComplementoAnotacoes: observable,
            detalhesProcessoApregoado: observable,
            objetoLocalStorage: computed,
            abrirModalConfirmacaoEditarVoto: action,
            fecharModalConfirmacaoEditarVoto: action,
            toggleFixarPainelJulgamento: action,
            resetAtributosProcessos: action,
            carregandoPartes: computed,
            carregandoJulgamentosAnteriores: computed,
            carregandoTramitacoesInternas: computed,
            idSessaoSelecionada: computed,
            descricaoProcessosEmExibicao: computed,
            processosApreciadosPorRelator: computed,
            processosPendentesPorRelator: computed,
            processosSessaoOrdenadosPreferencias: computed,
            processosPendentes: computed,
            processosPendentesFiltrados: computed,
            processosApreciados: computed,
            processosApreciadosFiltrados: computed,
            advogadosParticipacaoVirtual: computed,
            advogadosSustentacaoOral: computed,
            inscricoesAdvogadosSustentacaoOral: computed,
            representantesComInscricaoParaSessaoPresencialComProcessos: computed,
            processosComPedidoDeParticipacaoDeRepresentante: computed,
            processosComPedidoDeParticipacaoPresencial: computed,
            processosComPedidoDeParticipacaoVirtual: computed,
            quantidadeProcessosPendentesComVistaEmMesa: computed,
            tipoResultadoJulgamentoJulgado: computed,
            processosEmExibicao: computed,
            carregarTranscendenciaBloqueioBaixa: action,
            carregarConteudoVoto: action,
            consultarVersoesMinutaVoto: action,
            carregarConteudosVersoesEmModoAssincrono: action,
            mesclarDadosVersoesMinutaVoto: action,
            consultarVersaoMinutaVoto: action,
            calcularDiffs: action,
            calcularEstatisticasMudancasTexto: action,
            carregarEditorVoto: action,
            carregarEmentaDecisao: action,
            inicializarRascunho: action,
            carregarRascunhoCertidao: action,
            orgaosJulgadoresColegiadosUsuario: computed,
            apregoar: action,
            cancelarPregao: action,
            handleChangeSessao: action,
            abrirProcessoPeloNumero: action,
            exibirProcessoPeloIndice: action,
            posicionarProcessosEmExibicaoPeloIndice: action,
            indiceProcessoExpandido: computed,
            expandirProximoProcesso: action,
            expandirProcessoAnterior: action,
            salvarRascunhoCertidao: action,
            salvarComplementoAnotacao: action,
            salvarAnotacao: action,
            salvarOrdenacaoSecretario: action,
            salvarSequenciaPreferenciaArbitraria: action,
            recarregarProcesso: action,
            recarregarListaProcessos: action,
            recarregarListaProcessosSemCache: action,
            exibirListaProcessos: action,
            julgarProcessos: action,
            julgar: action,
            magistrados: computed,
            renumerarObservacoes: action,
            getTipoResultadoJulgamento: action,
            opcoesRelatores: computed,
            opcoesRelatoresApregoamento: computed,
            recuperarPendenciasSessao: action,
            tratarProcessosNaoLiberados: action,
            isSessaoFinalizada: computed,
            marcarAdvogadosAusentesLocalParaProcessosApregoaveisEmPlanilha: action,
            marcarAdvogadosAusentes: action,
            marcarAdvogadosAusentesEmLoteUmAUm: action,
            isPermitidaApreciacaoProcessos: computed,
            isPermitidaApreciacaoProcessosPJe: computed,
            carregarMovimentosDecisoesSessao: action,
            recuperarUrlEdicaoVoto: action,
            reset: override,
            processosFiltrados: override,
            mapaProcessosPartes: override
        });

        this.createReaction(
            reaction(
                () => this.idProcessosEmExibicao,
                () => (this.processoScroll = undefined)
            )
        );
        this.createReaction(
            reaction(() => this.sessaoStore.sessaoSelecionada, this.carregarInformacoesSessaoSelecionada)
        );
        this.createReaction(
            reaction(
                () => this.processoModal,
                (processoModal) => {
                    if (processoModal) {
                        this.carregarDetalhes(processoModal);
                    }
                }
            )
        );
        this.createReaction(
            reaction(
                () => this.processoExpandido,
                (processoExpandido) => {
                    if (processoExpandido) {
                        this.carregarDetalhes(processoExpandido);
                    }
                }
            )
        );
    }

    get storageType() {
        return LOCALSTORAGE_STORAGE;
    }

    async inicializar() {
        super.inicializar();
        this.inicializarFiltros(() => new FiltrosSalaSessao(this));
    }

    get objetoLocalStorage() {
        return {
            nomeStore: 'SalaSessaoStore',
            confirmarEdicaoVoto: this.confirmarEdicaoVoto === undefined ? true : this.confirmarEdicaoVoto,
            fecharProgressoAutomaticamente:
                this.fecharProgressoAutomaticamente === undefined ? false : this.fecharProgressoAutomaticamente,
            painelInicioCertidaoExpandido: this.painelInicioCertidaoExpandido,
            painelDecisaoCertidaoExpandido: this.painelDecisaoCertidaoExpandido,
            painelPartesCertidaoExpandido: this.painelPartesCertidaoExpandido
        };
    }

    abrirModalConfirmacaoEditarVoto() {
        this.modalConfirmacaoEditarVoto = true;
    }

    fecharModalConfirmacaoEditarVoto() {
        this.modalConfirmacaoEditarVoto = false;
    }

    toggleFixarPainelJulgamento() {
        this.fixarPainelJulgamento = !this.fixarPainelJulgamento;
    }

    reset() {
        super.reset();
        this.resetAtributosProcessos();
    }

    resetAtributosProcessos() {
        this.sessaoStore.processoApregoado = undefined;
        this.sessaoStore.relatorPregaoLote = undefined;
        this.processoExpandido = undefined;
        this.processoModal = undefined;
        this.processoScroll = undefined;
        this.idProcessosEmExibicao = 'P';
        this.abaSelecionadaDetalhesProcesso = ABAS_DETALHES_PROCESSO.PROCESSO.id;
        this.offsetScroll = {};
        this.carregandoMovimentosDecisoes = false;
    }

    get carregandoPartes() {
        return this.sessaoStore.carregandoPartes;
    }

    get carregandoJulgamentosAnteriores() {
        return this.sessaoStore.carregandoJulgamentosAnteriores;
    }

    get idSessaoSelecionada() {
        return this.sessaoStore?.idSessaoSelecionada;
    }

    get carregandoTramitacoesInternas() {
        return this.sessaoStore.carregandoTramitacoesInternas;
    }

    carregarDetalhes(processo) {
        this.carregarDetalhesProcesso(processo);
        this.carregarAlertasImpedimento(processo);
    }

    carregarInformacoesSessaoSelecionada = () => {
        if (this.rootStore.routerStore.isSalaSessao && this.sessaoStore.sessaoSelecionada) {
            this.configurarProcessoExpandido();
            this.recuperarProcuradoresSessoes();
            this.sessaoStore.carregarPartesSessao(this.idSessaoSelecionada);
            this.sessaoStore.carregarJulgamentosAnteriores();
            this.sessaoStore.carregarMovimentacoesInternas(this.idSessaoSelecionada);
            this.carregarMovimentosDecisoesSessao();
        }
    };

    async recuperarProcuradoresSessoes() {
        for (let index in this.sessoesSessaoSelecionada.slice()) {
            let sessao = this.sessoesSessaoSelecionada[index];

            if (sessao?.id) {
                let procurador = await this.sessaoService.recuperarProcurador(sessao.id);
                runInAction(() => {
                    sessao.procurador = procurador?.codigo ? procurador : undefined;

                    if (this.isSessaoPJe(sessao.id)) {
                        this.getProcessosSessao(sessao.id).forEach(
                            (registro) => (registro.procurador = sessao.procurador)
                        );
                    }
                });
            }
        }
    }

    get opcoesProcessosEmExibicao() {
        return [
            {
                id: 'P',
                ordem: 0,
                descricao: 'pendentes',
                descricaoSingular: 'Pendente',
                descricaoAbreviada: 'pend',
                funcaoProcessos: () => this.processosPendentes,
                funcaoProcessosFiltrados: () => this.processosPendentesFiltrados
            },
            {
                id: 'A',
                ordem: 1,
                descricao: 'apreciados',
                descricaoSingular: 'Apreciado',
                descricaoAbreviada: 'aprec',
                funcaoProcessos: () => this.processosApreciados,
                funcaoProcessosFiltrados: () => this.processosApreciadosFiltrados
            }
        ];
    }

    get descricaoProcessosEmExibicao() {
        let opcaoProcessosEmExibicao = this.opcoesProcessosEmExibicao.find(
            (opcaoProcessos) => opcaoProcessos.id === this.idProcessosEmExibicao
        );
        return opcaoProcessosEmExibicao ? opcaoProcessosEmExibicao.descricao : '';
    }

    processosApreciadosRelator(siglaRelator) {
        if (siglaRelator) {
            return this.processosApreciadosPorRelator.get(siglaRelator);
        }
        return [];
    }

    get processosApreciadosPorRelator() {
        let retorno = new Map();
        this.relatoresSessao.forEach((relator) => {
            retorno.set(relator, this.filtros.filtroNomeRelator.filtrar(this.processosApreciados, relator));
        });
        return retorno;
    }

    processosPendentesRelator(relator) {
        if (relator) {
            return this.processosPendentesPorRelator.get(relator);
        }
        return [];
    }

    get processosPendentesPorRelator() {
        let retorno = new Map();
        this.relatoresSessao.forEach((relator) => {
            retorno.set(relator, this.filtros.filtroNomeRelator.filtrar(this.processosPendentes, relator));
        });
        return retorno;
    }

    isMagistradoImpedido(siglaMagistrado, registro) {
        return this.sessaoStore.isMagistradoImpedido(siglaMagistrado, registro);
    }

    getImpedimentosComposicaoPadrao(registro) {
        let retorno = [];

        if (this.sessaoStore.sessaoSelecionada.composicaoPadrao) {
            retorno = this.sessaoStore.sessaoSelecionada.composicaoPadrao.reduce((acumulador, composicao) => {
                if (composicao.titular && this.isMagistradoImpedido(composicao.titular.sigla, registro)) {
                    acumulador.push({
                        sigla: composicao.titular.sigla,
                        genero: composicao.titular.genero
                    });
                }
                return acumulador;
            }, []);
        }
        return retorno;
    }

    ordenarProcessos = () => {
        this.sessaoService.ordenarProcessos(this.sessaoStore.sessaoSelecionada.id);
        //o recebimento do resultado da ordenação será tratada pelo envelope
    };

    getProcessosSessao(sessaoId) {
        return this.processosSessao.filter((registro) => registro.sessaoId === sessaoId);
    }

    get processosSessaoOrdenadosPreferencias() {
        if (this.sessaoStore?.sessaoSelecionada?.processos) {
            return this.sessaoStore.sessaoSelecionada.processos.slice().sort((r1, r2) => {
                let s1 =
                    r1.informacaoProcessoSalaSessao && r1.informacaoProcessoSalaSessao.sequenciaPreferencia > -9999
                        ? r1.informacaoProcessoSalaSessao.sequenciaPreferencia
                        : 9999;
                let s2 =
                    r2.informacaoProcessoSalaSessao && r2.informacaoProcessoSalaSessao.sequenciaPreferencia > -9999
                        ? r2.informacaoProcessoSalaSessao.sequenciaPreferencia
                        : 9999;
                return s1 - s2;
            });
        }

        return [];
    }

    get processosPendentes() {
        let processos = [];

        if (this.processosSessao) {
            processos = this.processosSessao.filter((registro) => !this.isProcessoApreciado(registro));
            processos = this.ordenarPorVistaMesa(processos);
        }
        return processos;
    }

    get processosPendentesFiltrados() {
        return this.filtros && this.filtros.filtrarProcessos(this.processosPendentes);
    }

    get processosApreciados() {
        let processos = [];

        if (this.processosSessao) {
            processos = this.processosSessao.filter((registro) => this.isProcessoApreciado(registro));
            processos = this.ordenarPorDataJulgamento(processos);
        }
        return processos;
    }

    ordenarPorDataJulgamento(processos) {
        return processos.slice().sort((r1, r2) => {
            if (
                r1.dataJulgamento &&
                r2.dataJulgamento &&
                moment(r1.dataJulgamento, 'DD-MM-YYYY-HH-mm-ss').isAfter(
                    moment(r2.dataJulgamento, 'DD-MM-YYYY-HH-mm-ss')
                )
            ) {
                return -1;
            }
            if (r1.dataJulgamento && !r2.dataJulgamento) {
                return -1;
            }
            return 0;
        });
    }

    ordenarPorVistaMesa(processos) {
        return processos.slice().sort((r1, r2) => {
            if (
                r1.informacaoProcessoSalaSessao.magistradoVistaEmMesa &&
                !r2.informacaoProcessoSalaSessao.magistradoVistaEmMesa
            ) {
                return -1;
            }
            if (
                !r1.informacaoProcessoSalaSessao.magistradoVistaEmMesa &&
                r2.informacaoProcessoSalaSessao.magistradoVistaEmMesa
            ) {
                return 1;
            }
            return 0;
        });
    }

    get processosApreciadosFiltrados() {
        return this.filtros && this.filtros.filtrarProcessos(this.processosApreciados);
    }

    get processosFiltrados() {
        return this.processosPendentesFiltrados.concat(this.processosApreciadosFiltrados);
    }

    get advogadosParticipacaoVirtual() {
        let advogados = [];

        this.sessaoStore.sessaoSelecionada &&
            this.processosSessao &&
            this.processosSessao.forEach((proc) => {
                proc.pedidoSustentacaoOral &&
                    proc.pedidoSustentacaoOral.pedidosDetalhados &&
                    proc.pedidoSustentacaoOral.pedidosDetalhados.forEach((pedido) => {
                        pedido.advogados &&
                            pedido.advogados.forEach((adv) => {
                                if (
                                    adv.modoParticipacao &&
                                    adv.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo
                                ) {
                                    advogados.push(adv);
                                }
                            });
                    });
            });

        let advogadosUnicos = this.removerDuplicados(advogados, 'nome');

        // TODO Ajustar quando o backend retornar informação de presença de advogados
        advogadosUnicos = advogadosUnicos.slice().sort((adv1, adv2) => {
            return adv1.nome.localeCompare(adv2.nome);
        });

        return advogadosUnicos;
    }

    get advogadosSustentacaoOral() {
        let advogados = [];

        this.sessaoStore.sessaoSelecionada &&
            this.processosSessao?.forEach((proc) => {
                proc.pedidoSustentacaoOral?.pedidosDetalhados?.forEach((pedido) => {
                    pedido?.advogados?.forEach((adv) => {
                        if (
                            adv.modoParticipacao &&
                            !ModoParticipacaoBusiness.isModoParticipacaoVirtual(adv.modoParticipacao) &&
                            adv.modoParticipacao !== MODO_PARTICIPACAO_ADVOGADO.PEDIDO_DESTAQUE.codigo
                        ) {
                            advogados.push(adv);
                        }
                    });
                });
            });

        let advogadosUnicos = this.removerDuplicados(advogados, 'nome');

        // TODO Ajustar quando o backend retornar informação de presença de advogados
        advogadosUnicos = advogadosUnicos.slice().sort((adv1, adv2) => {
            return adv1.nome.localeCompare(adv2.nome);
        });

        return advogadosUnicos;
    }

    get inscricoesAdvogadosSustentacaoOral() {
        const inscricoes = {};
        Array.from(this.representantesComInscricaoParaSessaoPresencialComProcessos.keys()).forEach((chaveAdvogado) => {
            this.representantesComInscricaoParaSessaoPresencialComProcessos
                .get(chaveAdvogado)
                ?.inscricoes.forEach((inscricao) => {
                    const chaveInscricao = this.getInscricaoKey(chaveAdvogado, inscricao);
                    const advogado = this.representantesComInscricaoParaSessaoPresencialComProcessos.get(chaveAdvogado);
                    inscricoes[chaveInscricao] = {
                        advogado: {
                            nome: advogado.nome,
                            genero: advogado.genero,
                            numRepresentante: advogado.numRepresentante,
                            regOAB: {
                                numero: advogado.regOAB.numero,
                                letra: advogado.regOAB.letra,
                                siglaUf: advogado.regOAB.siglaUf
                            }
                        },
                        ...inscricao
                    };
                });
        });

        const inscricoesOrdenadas = {};
        Object.keys(inscricoes)
            .sort((adv1, adv2) => {
                return adv1.localeCompare(adv2);
            })
            .forEach((chaveInscricao) => {
                inscricoesOrdenadas[chaveInscricao] = inscricoes[chaveInscricao];
            });

        return inscricoesOrdenadas;
    }

    alteraAtributoAdvogado(value, inscricao, atributo) {
        const chaveAdvogado = this.getRepresentanteKey(inscricao.advogado);
        const advogadoComProcessos =
            this.representantesComInscricaoParaSessaoPresencialComProcessos?.get(chaveAdvogado);

        if (advogadoComProcessos !== undefined) {
            const inscricaoIndex = advogadoComProcessos.inscricoes.findIndex(
                (inscricaoStore) =>
                    inscricaoStore.sessaoId === inscricao.sessaoId &&
                    inscricaoStore.numeroCompletoProcesso === inscricao.numeroCompletoProcesso &&
                    inscricaoStore.codigoDaParte === inscricao.codigoDaParte
            );
            runInAction(() => {
                advogadoComProcessos.inscricoes[inscricaoIndex][atributo] = value;
            });
        }
    }

    /**
     * Chave única para identificar o advogado.
     *
     * @param {object} adv Advogado
     * @returns {string} Chave única para o advogado.
     */
    getRepresentanteKey(adv) {
        if (adv.nome && adv.regOAB && adv.regOAB.numero && adv.regOAB.siglaUf) {
            return `${adv.nome.trim().toUpperCase()}|${adv.regOAB.numero}${adv.regOAB.letra ? `-${adv.regOAB.letra}` : ''}/${adv.regOAB.siglaUf}`;
        }

        console.error('Falha ao gerar chave para o advogado ' + adv.nome);
        return '';
    }

    /**
     * Chave única para identificar inscrição do advogado.
     *
     * @param {string} chaveAdvogado Chave única para o advogado.
     * @param {object} inscricao Inscrição
     * @returns {string} Chave única para a inscrição do advogado.
     */
    getInscricaoKey(chaveAdvogado, inscricao) {
        if (inscricao.sessaoId && inscricao.numeroCompletoProcesso && inscricao.codigoDaParte) {
            return `${chaveAdvogado}|${inscricao.sessaoId}|${inscricao.numeroCompletoProcesso}|${inscricao.codigoDaParte}`;
        }

        console.error(
            'Falha ao gerar chave de inscrição para o advogado: ' + chaveAdvogado + '|' + JSON.stringify(inscricao)
        );
        return '';
    }

    /**
     * Retorna as presenças e os modos de participação de um advogado na sessão.
     *
     * @param {string} chaveAdvogado Chave gerada de acordo com o formato definido
     * @returns {Object}
     */
    getPresencasModosParticipacao(chaveAdvogado) {
        const modosParticipacao = new Set();
        const presencas = new Set();

        this.representantesComInscricaoParaSessaoPresencialComProcessos.forEach(
            (advogadoComProcessos, chaveAdvogadoComProcessos) => {
                if (chaveAdvogadoComProcessos === chaveAdvogado) {
                    for (const inscricao of advogadoComProcessos.inscricoes.values()) {
                        modosParticipacao.add(inscricao.modoParticipacao);
                        presencas.add(inscricao.presente);
                    }
                }
            }
        );

        return { presencas, modosParticipacao };
    }

    get representantesComInscricaoParaSessaoPresencialComProcessos() {
        const modosParticipacaoExcecao = [
            MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo,
            MODO_PARTICIPACAO_ADVOGADO.SUSTENTACAO_ORAL_ELETRONICA.codigo,
            MODO_PARTICIPACAO_ADVOGADO.PEDIDO_DESTAQUE.codigo
        ];

        return this.representantesComInscricao(modosParticipacaoExcecao);
    }

    representantesComInscricao(modosParticipacaoExcecao) {
        let representantes = new Map();

        this.sessaoStore.sessaoSelecionada &&
            this.processosSessao &&
            this.processosSessao.forEach((proc) => {
                proc.pedidoSustentacaoOral &&
                    proc.pedidoSustentacaoOral.pedidosDetalhados &&
                    proc.pedidoSustentacaoOral.pedidosDetalhados.forEach((pedido) => {
                        pedido.advogados &&
                            pedido.advogados.forEach((adv) => {
                                if (adv.modoParticipacao && !modosParticipacaoExcecao.includes(adv.modoParticipacao)) {
                                    let chave = this.getRepresentanteKey(adv);
                                    let representante;

                                    if (representantes.has(chave)) {
                                        representante = representantes.get(chave);
                                    } else {
                                        representante = new RepresentanteParte();
                                    }

                                    representante.genero = adv.genero;
                                    representante.nome = adv.nome;
                                    representante.numRepresentante = adv.numRepresentante;
                                    representante.regOAB = {
                                        numero: adv.regOAB && adv.regOAB.numero ? adv.regOAB.numero : '',
                                        letra: adv.regOAB && adv.regOAB.letra ? adv.regOAB.letra : '',
                                        siglaUf: adv.regOAB && adv.regOAB.siglaUf ? adv.regOAB.siglaUf : ''
                                    };

                                    let inscricao = new InscricaoRepresentanteParte();
                                    inscricao.idInscricao = adv.idInscricao;
                                    inscricao.sessaoId = this.sessaoStore.sessaoSelecionada.id;
                                    inscricao.numeroCompletoProcesso = proc.processo.numeroCompleto;

                                    inscricao.sequencialApreciacaoProcesso = pedido.sequencia;

                                    inscricao.denominacaoDaParte = pedido.denominacaoDaParte;
                                    inscricao.nomeDaParte = pedido.nomeDaParte;
                                    inscricao.codigoDaParte = pedido.codigoDaParte;

                                    inscricao.dataSolicitacaoSustentacao = adv.dataSolicitacaoSustentacao;
                                    inscricao.fasePreferencialSustentacao = adv.fasePreferencialSustentacao;
                                    inscricao.modoParticipacao = adv.modoParticipacao;
                                    inscricao.preferencia = adv.preferencia;
                                    inscricao.presente = adv.presente;
                                    inscricao.sequencialPreferencia = adv.sequencialPreferencia;
                                    inscricao.sustentacaoOral = adv.sustentacaoOral;

                                    if (representante.inscricoes === undefined) {
                                        representante.inscricoes = [];
                                    }

                                    representante.inscricoes.push(inscricao);
                                    representantes.set(chave, representante);
                                }
                            });
                    });
            });

        return representantes;
    }

    get processosComPedidoDeParticipacaoDeRepresentante() {
        let processos = [];

        this.processosSessao.forEach((proc) => {
            if (
                proc.pedidoSustentacaoOral !== undefined &&
                proc.pedidoSustentacaoOral.pedidosDetalhados !== undefined
            ) {
                processos.push(proc);
            }
        });

        return processos;
    }

    get processosComPedidoDeParticipacaoPresencial() {
        return this.sustentacoesService.processosComPedidoDeParticipacaoPresencial(this.processosSessao);
    }

    findProcessosNaoApreciadosComAdvogadosNaoAusentes(siglaRelator = undefined, verificarRegrasPregaoPlanilha = true) {
        const processos = [];

        this.processosSessao.forEach((proc) => {
            if (
                !this.isProcessoApreciado(proc) &&
                (!verificarRegrasPregaoPlanilha || this.podeApregoarNaPlanilha(proc)) &&
                this.temInscricaoNaoAusente(proc, siglaRelator)
            ) {
                processos.push(proc);
            }
        });

        return processos;
    }

    temInscricaoNaoAusente(registro, siglaRelator = undefined) {
        const estaNaPlanilhaDoRelator = !!(siglaRelator && registro.processo?.relator?.sigla === siglaRelator);
        const temPedidosDetalhados = !!registro.pedidoSustentacaoOral?.pedidosDetalhados;

        if ((siglaRelator && !estaNaPlanilhaDoRelator) || !temPedidosDetalhados) {
            return false;
        }

        return registro.pedidoSustentacaoOral.pedidosDetalhados?.some((pedido) => {
            return pedido.advogados?.some((advogado) => {
                return (
                    ![
                        MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo,
                        MODO_PARTICIPACAO_ADVOGADO.SUSTENTACAO_ORAL_ELETRONICA.codigo
                    ].includes(advogado.modoParticipacao) &&
                    advogado.presente !== SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo
                );
            });
        });
    }

    get processosComPedidoDeParticipacaoVirtual() {
        return this.sustentacoesService.findProcessosComPedidoDeParticipacaoVirtual(this.processosSessao);
    }

    temPartipacaoSessao(proc) {
        return this.sustentacoesService.temPartipacaoSessao(proc);
    }

    temPartipacaoVirtual(proc) {
        return this.sustentacoesService.temPartipacaoVirtual(proc);
    }

    processoComVistaEmMesa(proc) {
        return !!proc?.informacaoProcessoSalaSessao?.magistradoVistaEmMesa;
    }

    get quantidadeProcessosPendentesComVistaEmMesa() {
        let quantidade = 0;

        if (this.processosSessao) {
            this.processosSessao.forEach((proc) => {
                if (this.processoComVistaEmMesa(proc) && !this.isProcessoApreciado(proc)) {
                    quantidade++;
                }
            });
        }

        return quantidade;
    }

    removerDuplicados = (array, propriedade) => {
        return array.filter((obj, pos, arr) => {
            return arr.map((mapObj) => mapObj[propriedade]).indexOf(obj[propriedade]) === pos;
        });
    };

    possuiDestaque(registro) {
        return this.filtros.filtroDestaques.getValorOpcao(registro);
    }

    filtrarPorOrigem(processos, origem) {
        return this.filtros['filtroOrigem'].filtrar(processos, origem);
    }

    get tipoResultadoJulgamentoJulgado() {
        return this.getTipoResultadoJulgamento(RESULTADO_JULGAMENTO.JULGADO.codigo);
    }

    get processosEmExibicao() {
        if (this.exibirProcessosComCertidao) {
            return this.processosApreciadosFiltrados.filter((proc) => proc.certidaoGerada) || [];
        } else {
            return (
                (this.idProcessosEmExibicao === 'P'
                    ? this.processosPendentesFiltrados
                    : this.processosApreciadosFiltrados) || []
            );
        }
    }

    isProcessoApreciado(registro) {
        return SituacaoJulgamentoProcessoBusiness.isProcessoApreciado(registro?.resultadoJulgamento);
    }

    isProcessoJulgado(registro) {
        return SituacaoJulgamentoProcessoBusiness.isProcessoJulgado(registro?.resultadoJulgamento);
    }

    async carregarTranscendenciaBloqueioBaixa(registro) {
        if (registro) {
            runInAction(() => {
                registro.carregandoIndicadoresProcesso = true;
            });

            try {
                let info = await this.processoService.recuperarInfoTranscendenciaBloqueioBaixa(
                    this.idSessaoSelecionada,
                    registro
                );

                runInAction(() => {
                    if (info) {
                        registro.bloqueioBaixa = info.bloqueioBaixaAutomatica;
                        registro.transcendencia = info.transcendencia;
                    }

                    if (registro.bloqueioBaixa == null) {
                        const indicadores = registro.processo?.indicadores;

                        registro.bloqueioBaixa =
                            indicadores?.some((ind) => ind.descricao === 'Remessa bloqueada') ?? false;
                    }
                });
            } finally {
                runInAction(() => {
                    registro.carregandoIndicadoresProcesso = false;
                });
            }
        }
    }

    async carregarConteudoVoto(registro) {
        if (registro?.processo?.numeroCompleto) {
            let conteudoVoto;

            if (registro.acordaoAssinado) {
                conteudoVoto = await this.processoService.recuperarConteudoAcordaoAssinado(
                    registro.processo.numeroCompleto,
                    registro.sessaoId
                );
            } else {
                conteudoVoto = await this.processoService.recuperarConteudoVoto(
                    registro.processo.numeroCompleto,
                    registro.sessaoId
                );
            }

            runInAction(() => (registro.conteudoVoto = conteudoVoto));
        }
    }

    /**
     * @param {Object} versaoMinutaGed
     * @param {*} idDbLocal
     * @returns
     */
    #metadadosGedToString(versaoMinutaGed, idDbLocal = undefined) {
        if (idDbLocal) {
            return `id:${idDbLocal}|gedId:${versaoMinutaGed.docId}|extensaoVersaoGed:${versaoMinutaGed.extensao}`;
        } else {
            return `gedId:${versaoMinutaGed.docId}|extensaoVersaoGed:${versaoMinutaGed.extensao}`;
        }
    }

    #getChaveUnicaDocGed(versaoMinuta) {
        return `${versaoMinuta.docId}|${versaoMinuta.extensao}`;
    }

    async consultarVersoesMinutaVoto(registro, tipoCarregamentoVersoes = MODO_CARREGAMENTO_VERSOES_MINUTA.TODAS) {
        try {
            if (!registro) {
                throw new ReferenceError(
                    `Parâmetro inválido para \`consultarVersoesMinutaVoto\`: registro não pode ser nulo.`
                );
            }

            if (!registro.sessaoId) {
                throw new ReferenceError(
                    `Parâmetro inválido para \`consultarVersoesMinutaVoto\`: sessaoId não pode ser nulo.`
                );
            }

            if (!registro.processo.numeroCompleto) {
                throw new ReferenceError(
                    `Parâmetro inválido para \`consultarVersoesMinutaVoto\`: processo.numeroCompleto não pode ser nulo.`
                );
            }

            let resposta;

            try {
                resposta = await this.sessaoService.consultarVersoesMinutaVoto(
                    registro.sessaoId,
                    registro.processo.numeroCompleto
                );
            } catch (error) {
                throw new Error(`Serviço de consulta de minutas de votos não respondeu.`, { cause: error });
            }

            if (!resposta) {
                throw new Error(`Serviço de consulta de versões de minutas de votos respondeu de forma inesperada.`);
            }

            let versoesMinuta = JSON.parse(JSON.stringify(resposta));
            console.log(`Total de versões da minuta de voto (resposta do backend): ${versoesMinuta.length}`);

            // Garante que as versões estejam ordenadas corretamente (EM ORDEM DECRESCENTE)
            versoesMinuta.sort((v1, v2) => {
                const dataV1 = dayjs(v1.ultimaAlteracao, Configuracao.FORMATO_DATAHORA_SERVICOS_PADRAO);
                const dataV2 = dayjs(v2.ultimaAlteracao, Configuracao.FORMATO_DATAHORA_SERVICOS_PADRAO);
                return dataV2.diff(dataV1);
            });

            const QUANTIDADE_MINIMA_DE_CONTEUDOS_A_CARREGAR = 2; // Necessário carregar pelo menos as duas primeiras versões para calcular o diff
            const temHash = (versaoMinuta) => !!versaoMinuta.etag;
            const peloMenosUmaVersaoNaoTemHash = !versoesMinuta.every(temHash);

            if (peloMenosUmaVersaoNaoTemHash) {
                console.log(`Pelo menos uma versão de minuta não tem hash (eTag).`);
            }

            let versoesParaListar;

            if (
                tipoCarregamentoVersoes === MODO_CARREGAMENTO_VERSOES_MINUTA.TODAS ||
                versoesMinuta.length <= QUANTIDADE_MINIMA_DE_CONTEUDOS_A_CARREGAR ||
                peloMenosUmaVersaoNaoTemHash
            ) {
                versoesMinuta = await this.carregarConteudosVersoesEmModoAssincrono(registro, versoesMinuta);

                if (EXIBIR_VERSOES_SEM_ALTERACAO_CONTEUDO) {
                    console.log(`Total de versões de minuta de voto: ${versoesMinuta.length}`);
                    versoesParaListar = versoesMinuta;
                } else {
                    versoesParaListar = versoesMinuta.filter((versao) => !versao.semAlteracaoConteudo);
                    console.log(
                        `Total de versões de minuta de voto distintas (de acordo com a comparação de conteúdo): ${versoesParaListar.length}`
                    );
                }
            } else {
                for (let i = 0; i < versoesMinuta.length - 1; i++) {
                    if (versoesMinuta[i].etag && versoesMinuta[i + 1].etag) {
                        if (versoesMinuta[i].etag === versoesMinuta[i + 1].etag) {
                            versoesMinuta[i].semAlteracaoConteudo = true;
                        }
                    }
                }

                let versoesCarregadas;

                if (EXIBIR_VERSOES_SEM_ALTERACAO_CONTEUDO) {
                    console.log(`Total de versões de minuta: ${versoesMinuta.length}`);

                    if (
                        tipoCarregamentoVersoes === MODO_CARREGAMENTO_VERSOES_MINUTA.APENAS_VERSOES_COM_ETAG_DIFERENTES
                    ) {
                        versoesCarregadas = await this.carregarConteudosVersoesEmModoAssincrono(
                            registro,
                            versoesMinuta,
                            versoesMinuta.length
                        );
                    } else if (tipoCarregamentoVersoes === MODO_CARREGAMENTO_VERSOES_MINUTA.SOB_DEMANDA) {
                        versoesCarregadas = await this.carregarConteudosVersoesEmModoAssincrono(
                            registro,
                            versoesMinuta,
                            QUANTIDADE_MINIMA_DE_CONTEUDOS_A_CARREGAR
                        );
                    }

                    versoesParaListar = versoesCarregadas;
                } else {
                    const versoesComAlteracaoDeConteudoDeAcordoComHash = versoesMinuta.filter(
                        (versao) => !versao.semAlteracaoConteudo
                    );
                    console.log(
                        `Total de versões de minuta de voto distintas (de acordo com a comparação de hash): ${versoesComAlteracaoDeConteudoDeAcordoComHash.length}`
                    );

                    if (
                        tipoCarregamentoVersoes === MODO_CARREGAMENTO_VERSOES_MINUTA.APENAS_VERSOES_COM_ETAG_DIFERENTES
                    ) {
                        versoesCarregadas = await this.carregarConteudosVersoesEmModoAssincrono(
                            registro,
                            versoesComAlteracaoDeConteudoDeAcordoComHash,
                            versoesComAlteracaoDeConteudoDeAcordoComHash.length
                        );
                    } else if (tipoCarregamentoVersoes === MODO_CARREGAMENTO_VERSOES_MINUTA.SOB_DEMANDA) {
                        versoesCarregadas = await this.carregarConteudosVersoesEmModoAssincrono(
                            registro,
                            versoesComAlteracaoDeConteudoDeAcordoComHash,
                            QUANTIDADE_MINIMA_DE_CONTEUDOS_A_CARREGAR
                        );
                    }

                    versoesParaListar = versoesCarregadas.filter((versao) => !versao.semAlteracaoConteudo);
                    console.log(
                        `Total de versões de minuta de voto distintas (de acordo com a comparação de conteúdo): ${versoesParaListar.length}`
                    );

                    if (versoesComAlteracaoDeConteudoDeAcordoComHash.length !== versoesParaListar.length) {
                        console.warn(`Totais diferentes! O serviço retornou hashes diferentes para conteúdos iguais!`);
                    }
                }
            }

            versoesMinuta = JSON.parse(JSON.stringify(versoesParaListar));
            versoesMinuta = await this.mesclarDadosVersoesMinutaVoto(registro, versoesMinuta);
            versoesMinuta = this.calcularDiffs(versoesMinuta);
            runInAction(() => (registro.versoesMinutaVoto = JSON.parse(JSON.stringify(versoesMinuta))));
        } catch (error) {
            runInAction(() => (registro.versoesMinutaVoto = null));
            throw new Error(`Falha inesperada ao tentar carregar as versões da minuta de voto.`, { cause: error });
        }
    }

    async carregarConteudosVersoesEmModoAssincrono(registro, versoesMinuta, totalVersoesACarregar) {
        if (!totalVersoesACarregar) {
            totalVersoesACarregar = versoesMinuta.length;
        }

        const promisesMap = new Map();

        // Carrega as versões para marcar registros de alterações exclusivamente em metadados de minutas (sem alterações nos textos)
        let contador = 0;
        versoesMinuta.forEach((versao) => {
            if (++contador <= totalVersoesACarregar) {
                const chave = this.#getChaveUnicaDocGed(versao);
                const promisseConsulta = this.consultarVersaoMinutaVoto(registro, versao, false); // Não grava no cache local na momento da consulta para armazenar a informação "semAlteracaoConteudo" corretamente
                promisesMap.set(chave, promisseConsulta);
            }
        });

        const versoesMinutaVotoComConteudosCarregados = await Promise.all(Array.from(promisesMap.values()));

        if (totalVersoesACarregar !== versoesMinutaVotoComConteudosCarregados.length) {
            console.warn(
                `O total de versões carregadas é diferente do total de versões esperadas (foram carregadas ${versoesMinutaVotoComConteudosCarregados.length}/${totalVersoesACarregar} versões de minutas).`
            );
            //throw new Error(`O total de versões carregadas é diferente do total de versões esperadas (foram carregadas ${versoesMinutaVotoComConteudosCarregados.length}/${totalVersoesACarregar} versões de minutas).`);
        }

        // Compara os textos para marcar as versões duplicadas
        let indice = 0;
        versoesMinuta.forEach((versao) => {
            if (indice < totalVersoesACarregar) {
                if (
                    indice < versoesMinutaVotoComConteudosCarregados.length - 1 &&
                    versoesMinutaVotoComConteudosCarregados[indice].statusCarregamento !==
                        SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FALHOU &&
                    versoesMinutaVotoComConteudosCarregados[indice + 1].statusCarregamento !==
                        SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FALHOU &&
                    versoesMinutaVotoComConteudosCarregados[indice].statusCarregamento !==
                        SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO &&
                    versoesMinutaVotoComConteudosCarregados[indice + 1].statusCarregamento !==
                        SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO &&
                    versoesMinutaVotoComConteudosCarregados[indice].conteudo !== undefined &&
                    versoesMinutaVotoComConteudosCarregados[indice + 1].conteudo !== undefined &&
                    versoesMinutaVotoComConteudosCarregados[indice].conteudo ===
                        versoesMinutaVotoComConteudosCarregados[indice + 1].conteudo
                ) {
                    versao.semAlteracaoConteudo = true;
                }
            }

            indice++;
        });

        return versoesMinuta;
    }

    async mesclarDadosVersoesMinutaVoto(registro, versoesMinuta) {
        await versoesMinuta.forEach(async (versao) => {
            console.log(
                `Mesclando informações entre a resposta do serviço e o cache local... [${registro.processo.numeroCompleto}|${this.#metadadosGedToString(versao)}]`
            );
            await this.armazenarVersaoMinutaVotoCache(registro.processo.numeroCompleto, versao);

            if (!versao.conteudo || !versao.semAlteracaoConteudo) {
                const versaoNoCache = await this.consultarCacheLocalVersoesMinutasVotos(
                    registro.processo.numeroCompleto,
                    versao
                );

                if (!versao.conteudo && versaoNoCache?.conteudo) {
                    versao.conteudo = versaoNoCache.conteudo;
                }

                if (!versao.semAlteracaoConteudo && versaoNoCache?.semAlteracaoConteudo) {
                    versao.semAlteracaoConteudo = versaoNoCache.semAlteracaoConteudo;
                }
            }
        });

        return versoesMinuta;
    }

    async consultarVersaoMinutaVoto(registro, versaoMinutaGed, armazenarCache = true) {
        if (!registro) {
            throw new ReferenceError(
                `Parâmetro inválido para \`consultarVersaoMinutaVoto\`: registro não pode ser nulo.`
            );
        }

        if (!versaoMinutaGed) {
            throw new ReferenceError(
                `Parâmetro inválido para \`consultarVersaoMinutaVoto\`: versaoMinutaGed não pode ser nulo.`
            );
        }

        const cache = await this.consultarCacheLocalVersoesMinutasVotos(
            registro.processo.numeroCompleto,
            versaoMinutaGed
        );

        if (cache?.conteudo) {
            runInAction(() => {
                versaoMinutaGed.statusCarregamento = SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO;
            });

            console.log(
                `Atualizando o conteúdo da minuta de voto em memória com o conteúdo encontrado no cache... [${registro.processo.numeroCompleto}|${this.#metadadosGedToString(versaoMinutaGed)}]`
            );
            runInAction(() => {
                versaoMinutaGed.conteudo = cache.conteudo;
                versaoMinutaGed.statusCarregamento = SITUACAO_CARREGAMENTO_VERSAO_MINUTA.CARREGADO_DO_CACHE;
            });
        } else if (
            versaoMinutaGed.statusCarregamento &&
            versaoMinutaGed.statusCarregamento === SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO
        ) {
            console.log(`Aguardando a finalização do carregamento da versão da minuta...`);
            await this.esperarCarregamentoVersaoMinutaVoto(versaoMinutaGed);
        } else {
            console.log(
                `Requisitando versão da minuta... [${registro.processo.numeroCompleto}|${this.#metadadosGedToString(versaoMinutaGed)}]`
            );
            runInAction(() => {
                versaoMinutaGed.statusCarregamento = SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO;
            });

            try {
                const resposta = await this.sessaoService.consultarVersaoMinutaVoto(
                    registro.sessaoId,
                    registro.processo.numeroCompleto,
                    versaoMinutaGed.docId,
                    versaoMinutaGed.extensao
                );

                if (resposta?.conteudo) {
                    runInAction(() => {
                        versaoMinutaGed.statusCarregamento = SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FINALIZADO;
                        versaoMinutaGed.conteudo = resposta.conteudo;
                    });
                } else {
                    throw new Error('Conteúdo da versão de minuta indisponível.');
                }
            } catch (error) {
                runInAction(() => {
                    versaoMinutaGed.conteudo = '';
                    versaoMinutaGed.statusCarregamento = SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FALHOU;
                    versaoMinutaGed.erroCarregamento = ErrorUtil.getMensagemErro(error);
                });
            } finally {
                if (armazenarCache) {
                    await this.armazenarVersaoMinutaVotoCache(registro.processo.numeroCompleto, versaoMinutaGed);
                }
            }
        }

        return versaoMinutaGed;
    }

    async esperarCarregamentoVersaoMinutaVoto(versaoMinutaVoto) {
        const INTERVALO_VERIFICACAO = 100;

        return await new Promise((resolve) => {
            const verificadorCarregamentoIntervalID = setInterval(verificarCarregamento, INTERVALO_VERIFICACAO);

            function verificarCarregamento() {
                //console.info(`Aguardando carregamento da versão da minuta de voto... [ID=${verificadorCarregamentoIntervalID}]`);
                if (versaoMinutaVoto.conteudo) {
                    console.info(`Conteúdo da versão da minuta carregado. [ID=${verificadorCarregamentoIntervalID}]`);
                    clearInterval(verificadorCarregamentoIntervalID);
                    resolve(true);
                } else if (versaoMinutaVoto.statusCarregamento === SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FALHOU) {
                    console.info(
                        `Erro ao tentar carregar a versão da minuta. [ID=${verificadorCarregamentoIntervalID}]`
                    );
                    clearInterval(verificadorCarregamentoIntervalID);
                    resolve(true);
                }
            }
        });
    }

    async armazenarVersaoMinutaVotoCache(numeroProcesso, versaoMinutaGed) {
        const tupla = new VersaoMinutaVoto(
            undefined,
            versaoMinutaGed.docId,
            versaoMinutaGed.extensao,
            versaoMinutaGed.ultimaAlteracao,
            versaoMinutaGed.etag,
            versaoMinutaGed.conteudo,
            versaoMinutaGed.semAlteracaoConteudo
        );

        const item = await this.consultarCacheLocalVersoesMinutasVotos(numeroProcesso, versaoMinutaGed);

        if (item?.idVersaoMinuta) {
            console.log(
                `O item já existe no cache local [${this.#metadadosGedToString(versaoMinutaGed, item.idVersaoMinuta)}]`
            );
            tupla.idVersaoMinuta = item.idVersaoMinuta;
            tupla.conteudo = item.conteudo ? item.conteudo : versaoMinutaGed.conteudo;
            tupla.semAlteracaoConteudo = item.semAlteracaoConteudo
                ? item.semAlteracaoConteudo
                : versaoMinutaGed.semAlteracaoConteudo;
        }

        try {
            let id;

            if (tupla.idVersaoMinuta) {
                id = await db.table('versaoMinutaVoto').put(tupla);
                console.log(
                    `Minuta de voto atualizada no cache com sucesso. [${this.#metadadosGedToString(versaoMinutaGed, id)}]`
                );
            } else {
                id = await db.table('versaoMinutaVoto').add(tupla);
                console.log(
                    `Minuta de voto adicionada ao cache com sucesso. [${this.#metadadosGedToString(versaoMinutaGed, id)}]`
                );
            }

            this.atualizarUltimoAcesso(versaoMinutaGed);
        } catch (error) {
            console.error(`Falha ao gravar a minuta de voto no cache local: ${ErrorUtil.getMensagemErro(error)}`);
        }
    }

    calcularDiffs(versoesMinuta) {
        for (let i = 0; i < versoesMinuta.length; i++) {
            if (!versoesMinuta[i].diff && versoesMinuta[i].conteudo) {
                let conteudoAntigo = '';

                if (i + 1 < versoesMinuta.length && versoesMinuta[i + 1].conteudo) {
                    conteudoAntigo = versoesMinuta[i + 1].conteudo;
                }

                const htmlDiff = HtmlDiff.execute(conteudoAntigo, versoesMinuta[i].conteudo);
                runInAction(() => (versoesMinuta[i].diff = htmlDiff));
                this.calcularEstatisticasMudancasTexto(versoesMinuta[i], htmlDiff);
            }
        }

        return versoesMinuta;
    }

    calcularEstatisticasMudancasTexto(versaoMinuta, htmlDiff = undefined) {
        if (!htmlDiff) {
            htmlDiff = versaoMinuta.htmlDiff ? versaoMinuta.htmlDiff : '';
        }

        // @ts-ignore
        const countInsercoes = (htmlDiff.match(/ins class="diff/g) || []).length;
        // @ts-ignore
        const countExclusoes = (htmlDiff.match(/del class="diff/g) || []).length;
        //const countInsercoes = (htmlDiff.match(/diffins/g) || []).length;
        //const countExclusoes = (htmlDiff.match(/diffdel/g) || []).length;
        //const countAlteracoes = (htmlDiff.match(/diffmod/g) || []).length;

        runInAction(() => {
            if (!versaoMinuta.erroCarregamento) {
                versaoMinuta.mudancas = {
                    totalInsercoes: countInsercoes || 0,
                    totalExclusoes: countExclusoes || 0
                };
            }
        });
    }

    static async excluirMinutasVotoDoCache(
        tempoExpiracaoMinutos = Configuracao.CACHE_MINUTAS_VOTOS_TEMPO_EXPIRACAO_MINUTOS
    ) {
        const dataHoraReferenciaExpiracaoCache = dayjs().subtract(tempoExpiracaoMinutos, 'minutes');
        console.log(`Data de expiração do cache de minutas de votos: ${dataHoraReferenciaExpiracaoCache.toString()}`);
        let totalItensExcluidos = 0;

        try {
            const itensSelecionadosParaExclusao = await db
                .table('versaoMinutaVotoControleAcesso')
                .filter((acesso) => dayjs(acesso.ultimoAcesso).isBefore(dataHoraReferenciaExpiracaoCache))
                .toArray();

            for (const item of itensSelecionadosParaExclusao) {
                const itensExcluidos = await db
                    .table('versaoMinutaVoto')
                    .filter((versao) => versao.docId === item.docId && versao.extensao === item.extensao)
                    .delete();

                if (itensExcluidos > 0) {
                    totalItensExcluidos++;
                }
            }
        } catch (error) {
            throw new Error(
                `Falha ao excluir dados do cache de minutas de votos: ${ErrorUtil.getMensagemErro(error)}`,
                { cause: error }
            );
        }

        return totalItensExcluidos;
    }

    async atualizarUltimoAcesso(versaoMinutaGed) {
        const tupla = new VersaoMinutaVotoControleAcesso(
            undefined,
            versaoMinutaGed.docId,
            versaoMinutaGed.extensao,
            dayjs().format()
        );

        const item = await db
            .table('versaoMinutaVotoControleAcesso')
            .filter((acesso) => acesso.docId === versaoMinutaGed.docId && acesso.extensao === versaoMinutaGed.extensao)
            .first();

        if (item?.idControleAcesso) {
            console.log(
                `O item de controle de acesso já existe no db local. [${this.#metadadosGedToString(versaoMinutaGed, item.idControleAcesso)}]`
            );
            tupla.idControleAcesso = item.idControleAcesso;
        }

        try {
            let id;

            if (tupla.idControleAcesso) {
                id = await db.table('versaoMinutaVotoControleAcesso').put(tupla);
                console.log(
                    `Controle de acesso atualizado no cache com sucesso. [${this.#metadadosGedToString(versaoMinutaGed, id)}]`
                );
            } else {
                id = await db.table('versaoMinutaVotoControleAcesso').add(tupla);
                console.log(
                    `Controle de acesso adicionado ao cache com sucesso. [${this.#metadadosGedToString(versaoMinutaGed, id)}]`
                );
            }
        } catch (error) {
            console.error(`Falha ao gravar o controle de acesso no cache local: ${ErrorUtil.getMensagemErro(error)}`);
            throw error;
        }
    }

    async consultarCacheLocalVersoesMinutasVotos(numeroProcesso, versaoMinutaGed) {
        console.log(
            `Consultando metadados da versão de minuta de voto no cache local... [${numeroProcesso}|${this.#metadadosGedToString(versaoMinutaGed)}]`
        );
        let item = null;

        try {
            item = await db
                .table('versaoMinutaVoto')
                .filter(
                    (versao) => versao.docId === versaoMinutaGed.docId && versao.extensao === versaoMinutaGed.extensao
                )
                .first();

            if (item) {
                console.log(
                    `Achou no cache local. [${this.#metadadosGedToString(versaoMinutaGed, item.idVersaoMinuta)}]`
                );
                this.atualizarUltimoAcesso(versaoMinutaGed);

                if (!item.conteudo) {
                    console.log(
                        `Porém, o conteúdo está vazio. [${this.#metadadosGedToString(versaoMinutaGed, item.idVersaoMinuta)}]`
                    );
                }
            } else {
                console.log(`NÃO achou no cache local. [${this.#metadadosGedToString(versaoMinutaGed)}]`);
            }
        } catch (error) {
            throw new Error(`Falha ao consultar o cache local: ${ErrorUtil.getMensagemErro(error)}`, { cause: error });
        }

        return item;
    }

    async carregarEditorVoto(registro) {
        console.log(
            `Consultando editor utilizado para elaborar a minuta de voto... [${registro.processo.numeroCompleto}]`
        );

        if (registro?.processo?.numeroCompleto) {
            const editorMinutaVoto = await this.processoService.recuperarEditorVoto(
                registro.processo.numeroCompleto,
                registro.sessaoId
            );
            runInAction(() => (registro.editorMinutaVoto = editorMinutaVoto));
        }
    }

    async carregarEmentaDecisao(registro) {
        console.log(`Consultando ementa e decisão... [${registro.processo.numeroCompleto}]`);
        let ementaDecisao;
        let planilha;

        if (registro?.processo?.numeroCompleto) {
            ementaDecisao = await this.processoService.recuperarEmentaDecisao(
                registro.numeroProcesso,
                registro.sessaoId
            );

            if (this.isComponenteHabilitado('PlanilhaPlenario')) {
                console.log(`Consultando ementa e decisão da planilha... [${registro.processo.numeroCompleto}]`);
                planilha = await this.processoService.recuperarPlanilha(registro.processo.numeroCompleto);
            }

            runInAction(() => {
                if (ementaDecisao) {
                    registro.ementaDecisaoVoto = undefined;
                    registro.observacaoGabinete = undefined;
                    registro.ementa = this.retirarTagsHtmlDaEmenta(ementaDecisao.ementa);
                    registro.decisao = this.retirarTagsHtmlDaDecisao(ementaDecisao.decisao);

                    if (planilha) {
                        if (planilha.ementa || planilha.decisao) {
                            registro.ementaDecisaoVoto = {};
                            registro.ementaDecisaoVoto.ementa = this.retirarTagsHtmlDaEmenta(ementaDecisao.ementa);
                            registro.ementaDecisaoVoto.decisao = this.retirarTagsHtmlDaDecisao(ementaDecisao.decisao);
                        }

                        registro.ementa = planilha.ementa ? this.retirarTagsHtmlDaEmenta(planilha.ementa) : '';
                        registro.decisao = planilha.decisao ? this.retirarTagsHtmlDaDecisao(planilha.decisao) : '';
                        registro.observacaoGabinete = planilha.observacao
                            ? this.retirarTagsHtmlDaObservacao(planilha.observacao)
                            : '';
                    }
                }
            });
        }
        return ementaDecisao;
    }

    async forcarAtualizacaoEmentaDecisao(registro) {
        if (!registro) {
            throw new TypeError(
                `Parâmetro inválido para \`forcarAtualizacaoEmentaDecisao\`: ${typeof registro}, esperado \`Object\`.`
            );
        }

        if (!registro?.processo?.numeroCompleto) {
            throw new Error(`Registro sem o número do processo.`);
        }

        await this.sessaoService.forcarAtualizacaoEmentaDecisao(registro.sessaoId, registro.processo.numeroCompleto);
    }

    retirarTagsHtmlDaDecisao(decisao) {
        decisao = this.retirarTags(decisao);
        return decisao || '';
    }

    retirarTagsHtmlDaEmenta(ementa) {
        ementa = this.retirarTags(ementa);
        return ementa || '';
    }

    retirarTagsHtmlDaObservacao(observacao) {
        observacao = this.retirarTags(observacao);
        return observacao || '';
    }

    retirarTags(texto) {
        let tmp = document.implementation.createHTMLDocument('New').body;
        tmp.innerHTML = texto;
        let styles = tmp.getElementsByTagName('style');

        if (styles.length > 0) {
            tmp.getElementsByTagName('style')[0].remove();
        }

        return tmp.textContent || tmp.innerText || '';
    }

    /**
     * Retira o trecho inicial do texto da decisão (ISTO POSTO).
     *
     * @param {string} decisao O texto da decisão
     * @returns {string} O texto com o trecho suprimido, que corresponde ao início do texto até a primeira ocorrência de "por unanimidade" ou "à unanimidade" ou "por maioria".
     */
    retirarInicioDecisao(decisao) {
        let resultado = (decisao + '').replace(/( )+/g, ' ').trim();
        let testes = [
            resultado.toLowerCase().indexOf('unanimidade'),
            resultado.toLowerCase().indexOf('por unanimidade'),
            resultado.toLowerCase().indexOf('i - por unanimidade'),
            resultado.toLowerCase().indexOf('à unanimidade'),
            resultado.toLowerCase().indexOf('por maioria')
        ];

        let pos = -1;

        for (let teste of testes) {
            if (pos === -1 || (teste > -1 && teste < pos)) {
                pos = teste;
            }
        }

        if (pos > 0) {
            return resultado.substring(pos);
        }

        return resultado;
    }

    async inicializarRascunho(registro, rascunhoAtual) {
        let rascunho = rascunhoAtual ? rascunhoAtual.trimEnd() : '';

        if (!registro.certidaoGerada && registro.liberadoPeloRelator) {
            rascunho = await this.prepararRascunho(registro, rascunhoAtual);
            await this.salvarRascunhoCertidao(registro, rascunho);
        }

        return rascunho;
    }

    async prepararRascunho(registro, rascunhoAtual) {
        let rascunho = '';

        if (registro.liberadoPeloRelator) {
            if (!registro.decisao) {
                await this.carregarEmentaDecisao(registro);
            }

            if (registro.decisao) {
                rascunho = this.retirarInicioDecisao(registro.decisao);
            }
        }

        if (rascunho) {
            rascunho = StringUtil.tratarCaracteresNaoLatin1(rascunho);
            rascunho = rascunho.trim();
        }

        if (rascunhoAtual) {
            rascunho += !rascunhoAtual.startsWith('\n') ? '\n' + rascunhoAtual : rascunhoAtual;
        }

        return rascunho;
    }

    async carregarRascunhoCertidao(registro) {
        let rascunho = '';

        if (registro) {
            const rascunhoAtual = await this.certidaoService.recuperarRascunhoCertidao(registro);

            if (rascunhoAtual.trim().startsWith('Observação') && registro.processo.origem === Configuracao.ORIGEM_PJE) {
                rascunho = await this.inicializarRascunho(registro, rascunhoAtual);
            } else if (rascunhoAtual && rascunhoAtual.trim() !== '') {
                rascunho = rascunhoAtual;
            } else {
                rascunho = await this.inicializarRascunho(registro);
            }

            runInAction(() => {
                if (!registro.rascunhoEmEdicao) {
                    registro.rascunhoCertidao = rascunho;
                }
            });
        }

        return rascunho;
    }

    get orgaosJulgadoresColegiadosUsuario() {
        return this.usuarioStore.usuario.orgaosJulgadoresColegiados || [];
    }

    apregoar = (registro) => {
        let processoFiltrado = this.processosFiltrados.find(
            (reg) => reg.processo.numeroCompleto === registro.processo.numeroCompleto
        );

        if (processoFiltrado) {
            this.processoExpandido = registro;
        }

        ApmVars.setTagNumeroProcesso(registro?.processo?.numeroCompleto);
        SentryUtil.setScope();
        this.sessaoService.apregoar(registro.processo.numeroCompleto, registro.sessaoId);
    };

    cancelarPregao = () => {
        ApmVars.setTagNumeroProcesso(this.sessaoStore.processoApregoado?.processo?.numeroCompleto);
        SentryUtil.setScope();
        this.sessaoService.cancelarPregao(
            this.sessaoStore.processoApregoado.processo.numeroCompleto,
            this.sessaoStore.processoApregoado.sessaoId
        );
    };

    handleChangeSessao = (event) => {
        this.sessaoStore.idSessaoSelecionada = event.target.value;
    };

    abrirProcessoPeloNumero = (numeroProcesso) => {
        let registro = this.processosSessao.find((reg) => reg.numeroProcesso === numeroProcesso);

        if (registro) {
            ApmVars.setTagNumeroProcesso(numeroProcesso);
            SentryUtil.setScope();
            this.abrirProcesso(registro);
        }
    };

    // Abre o processo e posiciona a listagem no processo
    exibirProcessoPeloIndice(index) {
        if (this.processosEmExibicao.length > 0) {
            index = index >= 0 ? index : 0;
            index = index > this.processosEmExibicao.length - 1 ? this.processosEmExibicao.length - 1 : index;
            this.processoExpandido = this.processosEmExibicao[index];
            this.processoScroll = this.processosEmExibicao[index];
        }
    }

    // Posiciona a listagem no processo a partir do índice
    posicionarProcessosEmExibicaoPeloIndice(index) {
        if (this.processosEmExibicao.length > 0) {
            index = index >= 0 ? index : 0;
            index = index > this.processosEmExibicao.length - 1 ? this.processosEmExibicao.length - 1 : index;
            this.processoScroll = this.processosEmExibicao[index];
        }
    }

    get indiceProcessoExpandido() {
        let indiceProcessoExpandido = -1;

        if (this.processoExpandido) {
            indiceProcessoExpandido = this.processosEmExibicao.findIndex(
                (registro) =>
                    // @ts-ignore
                    registro.processo.numeroCompleto.indexOf(this.processoExpandido.processo.numeroCompleto) !== -1
            );
        }

        return indiceProcessoExpandido;
    }

    expandirProximoProcesso() {
        this.exibirProcessoPeloIndice(this.indiceProcessoExpandido + 1);
    }

    expandirProcessoAnterior() {
        this.exibirProcessoPeloIndice(this.indiceProcessoExpandido - 1);
    }

    async salvarRascunhoCertidao(registro, conteudo) {
        conteudo = StringUtil.tratarCaracteresNaoLatin1(conteudo);
        await this.certidaoService.salvarRascunhoCertidao(registro, conteudo);
        runInAction(() => {
            registro.rascunhoCertidao = conteudo;
            registro.rascunhoEmEdicao = false;
        });
    }

    salvarComplementoAnotacao(registro, anotacoes) {
        return this.anotacaoService.salvarComplementoAnotacao(
            registro.sessaoId,
            registro.processo.numeroCompleto,
            anotacoes,
            this.clienteEnvelopeId
        );
    }

    salvarAnotacao(registro, anotacoes) {
        return this.anotacaoService.salvarAnotacao(
            registro.sessaoId,
            registro.processo.numeroCompleto,
            anotacoes,
            this.clienteEnvelopeId
        );
    }

    salvarOrdenacaoSecretario(registro, ordenacaoSecretario) {
        return this.sessaoService.salvarOrdenacaoSecretario(
            registro.sessaoId,
            registro.processo.numeroCompleto,
            ordenacaoSecretario,
            this.clienteEnvelopeId
        );
    }

    salvarSequenciaPreferenciaArbitraria(registro, sequenciaPreferencia) {
        return this.sessaoService.salvarSequenciaPreferenciaArbitraria(
            registro.sessaoId,
            registro.processo.numeroCompleto,
            sequenciaPreferencia
        );
    }

    async recarregarProcesso(registro, ignorarCacheServidor = false, aguardarCarregamento = false) {
        if (aguardarCarregamento) {
            await this.sessaoStore.recarregarProcesso(registro, ignorarCacheServidor);
        } else {
            this.sessaoStore.recarregarProcesso(registro, ignorarCacheServidor);
        }
    }

    async recarregarListaProcessos() {
        await this.sessaoStore.recarregarListaProcessos();
    }

    async recarregarListaProcessosSemCache() {
        await this.sessaoStore.recarregarListaProcessosSemCache();
    }

    exibirListaProcessos(processoScroll) {
        if (this.isProcessoApreciado(processoScroll)) {
            this.idProcessosEmExibicao = 'A';
        } else {
            this.idProcessosEmExibicao = 'P';
        }
    }

    async julgarProcessos(processos, tipoResultadoJulgamento, vistas, ocultarProcessamentoLote) {
        if (!ocultarProcessamentoLote) {
            this.inicializarProcessamentoLote(processos.length, 'Apreciando processos');
        }

        let tratamentoErroComponente = !ocultarProcessamentoLote;
        let processosPorSessao = this.separarProcessosPorSessao(processos);
        let lotesProcessos = this.separarProcessosEmLotes(processosPorSessao);
        let resultadoProcessamento = await this.executarProcessamentoLote({
            lotesProcessos: lotesProcessos,
            qtdPromisesParalelas: 1,
            funcaoOperacao: (mapa) =>
                this.registrarJulgamentos(
                    mapa.processos,
                    mapa.sessaoId,
                    tipoResultadoJulgamento,
                    vistas,
                    tratamentoErroComponente
                )
        });

        if (this.finalizarProcessamentoLote(resultadoProcessamento)) {
            this.rootStore.mensagemStore.abrirMensagemSucesso('Operação realizada com sucesso.');
        } else {
            console.error('Falha na operação [ julgarProcessos() ].');
        }

        this.carregarMovimentosDecisoesSessao();

        return resultadoProcessamento;
    }

    separarProcessosEmLotes(processosPorSessao) {
        const qtdProcessosLote = 20;
        let lotes = [];

        for (let sessaoId in processosPorSessao) {
            let processosSessao = processosPorSessao[sessaoId];

            while (processosSessao.length) {
                lotes.push({
                    sessaoId: sessaoId,
                    processos: processosSessao.splice(0, qtdProcessosLote)
                });
            }
        }
        return lotes;
    }

    separarProcessosPorSessao(processos) {
        return processos.reduce((acumulador, registro) => {
            if (acumulador[registro.sessaoId] === undefined) {
                acumulador[registro.sessaoId] = [];
            }
            acumulador[registro.sessaoId].push(registro);
            return acumulador;
        }, {});
    }

    async registrarJulgamentos(processos, sessaoId, tipoResultadoJulgamento, vistas, tratamentoErroComponente) {
        let resultadoProcessamento = await this.processoService.registrarJulgamento(
            processos,
            sessaoId,
            tipoResultadoJulgamento,
            vistas,
            tratamentoErroComponente
        );
        await this.atualizarProcessosJulgamentoLote(resultadoProcessamento, tipoResultadoJulgamento, vistas);
        return resultadoProcessamento;
    }

    async atualizarProcessosJulgamentoLote(resultadoProcessamento, tipoResultadoJulgamento, vistas) {
        if (resultadoProcessamento) {
            for (let resultado of resultadoProcessamento) {
                if (!this.isResultadoErro(resultado)) {
                    let registro = this.sessaoStore.sessaoSelecionada.processos.find(
                        (reg) => reg.numeroProcesso === resultado.numeroProcesso
                    );

                    if (registro) {
                        await this.atualizarProcessoJulgamento(registro, tipoResultadoJulgamento, vistas, resultado);
                    }
                }
            }

            this.atualizarProcessamentoLote(resultadoProcessamento);
        }
    }

    async atualizarProcessoJulgamento(registro, tipoResultadoJulgamento, vistas, resultado) {
        await this.registrarObservacaoRascunhoCertidao(registro, tipoResultadoJulgamento, vistas);

        runInAction(() => {
            registro.sequencialApregoamento = resultado.sequencialApregoamento;
            registro.dataJulgamento = resultado.dataJulgamento;
        });
    }

    notificarJulgamentoLote(processos) {
        let idSessoes = processos.reduce((acumulador, registro) => {
            if (acumulador.findIndex((sessaoId) => sessaoId === registro.sessaoId) === -1) {
                acumulador.push(registro.sessaoId);
            }
            return acumulador;
        }, []);

        idSessoes.forEach((idSessao) =>
            this.sessaoService.notificarJulgamentoLote(idSessao, this.clienteEnvelopeService.clienteEnvelopeId)
        );
    }

    async julgar(registro, tipoJulgamento, vistas) {
        this.processoExpandido = undefined;
        this.processoScroll = undefined;
        this.processoModal = undefined;

        const descricaoAcaoEmLote = tipoJulgamento.julgado
            ? 'Apreciando processo'
            : 'Desfazendo apreciação de processo';

        const listaProcessosSelecionados = [registro];
        this.inicializarProcessamentoLote(listaProcessosSelecionados.length, descricaoAcaoEmLote, false, true);
        let resultadoProcessamento = await this.registrarJulgamentos(
            listaProcessosSelecionados,
            registro.sessaoId,
            tipoJulgamento,
            vistas
        );
        let sucesso = this.finalizarProcessamentoLoteSemTelaProgresso(resultadoProcessamento);

        if (sucesso) {
            registro.resultadoJulgamento = {
                codigo: tipoJulgamento.codigo,
                descricao: tipoJulgamento.descricao,
                julgado: tipoJulgamento.julgado
            };

            registro.dataJulgamento =
                tipoJulgamento.codigo !== RESULTADO_JULGAMENTO.NAO_JULGADO.codigo
                    ? moment(new Date()).format('DD-MM-YYYY-HH-mm-ss')
                    : undefined;

            this.sessaoStore.recuperarMovimentosDecisoes(registro);
            let proximoProcesso = this.getProximoProcessoLista(
                registro.processo.numeroCompleto,
                this.processosPendentesFiltrados
            );
            this.abrirProcesso(proximoProcesso);
        } else {
            if (this.processoExpandido === undefined) {
                this.abrirProcesso(registro);
            }

            console.error(`Falha na apreciação do processo ${registro.processo.numeroCompleto}.`);
        }

        return sucesso;
    }

    getProximoProcessoLista(numeroProcesso, processos) {
        let indiceProcessoAtual = processos.findIndex(
            (reg) => reg.processo.numeroCompleto.indexOf(numeroProcesso) > -1
        );
        let indiceProximoProcesso;

        if (processos.length > 1) {
            if (indiceProcessoAtual === processos.length - 1) {
                indiceProximoProcesso = indiceProcessoAtual - 1;
            } else {
                indiceProximoProcesso = indiceProcessoAtual + 1;
            }
        }

        return processos[indiceProximoProcesso];
    }

    // Só registra a observação se houver texto correspondente ao tipoJulgamento
    // em Configuracao.TXT_CERTIDAO_RESULTADOS
    async registrarObservacaoRascunhoCertidao(registro, tipoResultadoJulgamento, vistas) {
        let complementos = [];

        switch (tipoResultadoJulgamento.codigo) {
            case RESULTADO_JULGAMENTO.ADIADO.codigo:
            case RESULTADO_JULGAMENTO.RETIRADO_DE_PAUTA.codigo:
            case RESULTADO_JULGAMENTO.SUSPENSO.codigo:
            case RESULTADO_JULGAMENTO.SOBRESTADO.codigo:
                if (registro.processo.relator) {
                    complementos = [
                        this.pronomeTratamentoComCargo(registro.processo.relator),
                        registro.processo.relator.nome || '',
                        this.descricaoRelator(registro.processo.relator)
                    ];
                }
                break;
            case RESULTADO_JULGAMENTO.VISTA_REGIMENTAL.codigo:
                if (registro.processo.relator) {
                    complementos = [
                        this.complementoVistores(vistas),
                        this.pronomeTratamentoComCargo(registro.processo.relator),
                        registro.processo.relator.nome || '',
                        this.descricaoRelator(registro.processo.relator)
                    ];
                }
                break;
            default:
                break;
        }

        await this.complementarRascunhoCertidao(registro, tipoResultadoJulgamento, complementos);
    }

    descricaoRelator(magistrado) {
        let descricao = 'Relator';

        if (!magistrado?.genero) {
            console.error(
                `Falha ao tentar ler o gênero do magistrado ${magistrado.nome} para definição da forma de tratamento do responsável pela relatoria.`
            );
            return descricao;
        }

        return descricao + (magistrado.genero.startsWith('F') ? 'a' : '');
    }

    complementoVistores(vistas) {
        let retorno = '';
        vistas = vistas || [];
        vistas.forEach((vistor, index) => {
            let magistrado = this.magistrados.find((mag) => mag.sigla === vistor.sigla);

            if (magistrado) {
                retorno += this.complementoVistor(magistrado);
                retorno += index === vistas.length - 2 ? ' e ' : ', ';
            } else {
                console.error(
                    'Magistrado ' + vistor + ' não encontrado na lista de magistrados da Secretaria Eletrônica.'
                );
            }
        });
        return retorno;
    }

    get magistrados() {
        return this.rootStore.dominioStore.magistradosComSigla;
    }

    complementoVistor(magistrado) {
        return `pel${this.pronomeTratamentoComCargo(magistrado)} ${magistrado.nome}`;
    }

    pronomeTratamentoComCargo(magistrado) {
        const sigla = magistrado ? magistrado.sigla : '';

        if (magistrado) {
            this.recuperarGeneroMagistrado(sigla, magistrado);
            const artigoGenero = magistrado?.genero?.startsWith('F') ? 'a' : 'o';
            return `${artigoGenero} ${this.montarAbreviacaoExcelentissimo(artigoGenero)} ${this.descricaoCargo(magistrado)}`;
        } else {
            console.error(`Magistrado ${sigla} não encontrado na lista de magistrados da Secretaria Eletrônica.`);
            return ' <MAGISTRADO NÃO CADASTRADO NO PJE> ';
        }
    }

    montarAbreviacaoExcelentissimo(artigoGenero) {
        const excecoesAbreviacao = this.rootStore.parametroStore.getParametro('EXCECOES_ABREVIACAO_EXCELENTISSIMO_ABL');

        if (excecoesAbreviacao && dayjs().isBefore(dayjs(excecoesAbreviacao.dataFimVigencia, 'YYYY-MM-DD'))) {
            if (excecoesAbreviacao?.valor) {
                const excecoesAbreviacaoArray = excecoesAbreviacao.valor.split(/[,;]/); // Valores separados por ',' ou ';'

                if (excecoesAbreviacaoArray.includes(this.sessaoStore.sessaoSelecionada.orgaoJudicante.sigla)) {
                    return `Exm${artigoGenero}.`;
                }
            }
        }
        return `Ex.m${artigoGenero}`;
    }

    recuperarGeneroMagistrado(sigla, magistrado) {
        if (!magistrado.genero) {
            let magistr = this.dominioStore.magistrados.find((mag) => mag.sigla === sigla);

            if (magistr) {
                magistrado.genero = magistr.genero || 'M';
            }
        }
    }

    descricaoCargo(magistrado) {
        let genero = magistrado.genero || '';
        let siglaCargo = magistrado.codigoCargo || '';
        let cargo = this.rootStore.dominioStore.getDescricaoCargo(siglaCargo, genero);
        return cargo ? cargo.descricaoCargo : '';
    }

    getQuantidadeObservacoes(conteudoCertidao) {
        let retorno = [];
        let re = /Observação \d+/g;
        let s = conteudoCertidao;
        let m;

        do {
            m = re.exec(s);

            if (m) {
                retorno.push(m[0]);
            }
        } while (m);

        return retorno.length;
    }

    renumerarObservacoes(conteudo) {
        let linhas = [];
        let rascunho = conteudo + '\n';
        let reLinha = /.*\n/g;
        let reObservacao = /Observação \d+/g;
        let linha;

        do {
            linha = reLinha.exec(rascunho);

            if (linha) {
                linhas.push(linha[0]);
            }
        } while (linha);

        let index = 0;
        let count = 1;
        let novaLinha = '';

        linhas.forEach((lin) => {
            novaLinha = lin.replace('\n', '');

            if (lin.match(reObservacao)) {
                novaLinha = novaLinha.replace(reObservacao, 'Observação ' + count++);
            }

            linhas[index] = novaLinha;
            index++;
        });

        return linhas.join('\n');
    }

    async complementarRascunhoCertidao(registro, tipoResultadoJulgamento, complementos) {
        let texto = Configuracao.TXT_CERTIDAO_RESULTADOS[tipoResultadoJulgamento.codigo];

        if (texto) {
            complementos.forEach((complemento, index) => {
                texto = texto.replace('{' + index + '}', complemento);
            });

            await this.adicionarTextoRascunhoCertidao(registro, texto);
        }
    }

    async adicionarTextoRascunhoCertidao(registro, texto) {
        let rascunho = await this.certidaoService.recuperarRascunhoCertidao(registro);
        rascunho += texto;
        await this.certidaoService.salvarRascunhoCertidao(registro, rascunho);
        runInAction(() => (registro.rascunhoCertidao = rascunho));
    }

    async desfazerAlteracaoRascunhoCertidao(registro, texto) {
        let rascunho = registro.rascunho.replace(new RegExp(texto, 'g'), '');
        await this.certidaoService.salvarRascunhoCertidao(registro, rascunho);
        runInAction(() => (registro.rascunhoCertidao = rascunho));
    }

    getTipoResultadoJulgamento(idTipoJulgamento) {
        return this.dominioStore.tiposResultadoJulgamento.find((tipo) => tipo.codigo === idTipoJulgamento);
    }

    get opcoesRelatores() {
        let opcoes = [];
        this.relatoresSessao.forEach((relator) => {
            this.adicionarOpcaoRelator(this.processosPendentesRelator(relator), opcoes, relator);
        });
        opcoes.sort((r1, r2) => (r1.antiguidade > r2.antiguidade ? 0 : -1));
        return opcoes;
    }

    get opcoesRelatoresApregoamento() {
        let opcoes = [];
        this.relatoresSessao.forEach((relator) => {
            const processosPendentes = this.processosPendentesRelator(relator);
            const processosPlanilha = processosPendentes.filter((registro) => this.podeApregoarNaPlanilha(registro));
            this.adicionarOpcaoRelator(processosPlanilha, opcoes, relator);
        });

        opcoes.sort((r1, r2) => (r1.antiguidade > r2.antiguidade ? 0 : -1));
        return opcoes;
    }

    podeApregoarNaPlanilha(registro) {
        const condicoesGerais =
            !registro.informacoesAdicionais.RetornoVistaRegimental ||
            registro.informacoesAdicionais.RetornoVistaRegimental.length === 0;

        if (registro.processo.origem === Configuracao.ORIGEM_PJE) {
            return condicoesGerais && this.isPermitidaApreciacaoProcessosPJe;
        } else {
            return condicoesGerais && !registro.necessitaImportacaoSalaSessao;
        }
    }

    adicionarOpcaoRelator(processos, opcoes, relator) {
        if (processos.length > 0) {
            opcoes.push({
                label: this.getLabelRelator(relator),
                value: relator,
                antiguidade: this.antiguidadeRelator(relator),
                qtdPendentes: processos.length,
                primeiroProcesso: processos[0].processo.numeroCompleto,
                ultimoProcesso: processos[processos.length - 1].processo.numeroCompleto
            });
        }
    }

    apreciadoComoJulgado(resultadoJulgamento) {
        return SituacaoJulgamentoProcessoBusiness.isProcessoJulgado(resultadoJulgamento);
    }

    async recuperarPendenciasSessao() {
        let sessoesPendencias;
        let statusSessao = this.sessaoStore?.sessaoSelecionada?.situacao;

        if (
            ((statusSessao === Configuracao.STATUS_ENCERRADA || statusSessao === Configuracao.STATUS_INICIADA) &&
                this.sessaoStore.sessaoSelecionada) ||
            this.existeSessaoSIJ
        ) {
            sessoesPendencias = await this.sessaoService.listarPendencias(this.sessaoStore.sessaoSelecionada.id);
            this.tratarProcessosNaoLiberados(sessoesPendencias);
        }

        return sessoesPendencias;
    }

    tratarProcessosNaoLiberados(sessoesPendencias) {
        // Detectando processos não liberados no frontend pois backend não traz essa informação
        let processosNaoLiberados = this.processosSessao.filter(
            (processo) => !processo.liberadoPeloRelator && this.apreciadoComoJulgado(processo.resultadoJulgamento)
        );
        processosNaoLiberados.forEach((processoNaoLiberado) => {
            let pendencias = sessoesPendencias.find((sessao) => sessao.sessao === processoNaoLiberado.sessaoId);

            if (pendencias) {
                if (pendencias.pendenciasProcessos === undefined) {
                    pendencias.pendenciasProcessos = [];
                }

                let processoComPendencia = pendencias.pendenciasProcessos.find((processo) => {
                    return processoNaoLiberado.processo.numeroCompleto === processo.numeroProcesso;
                });

                if (processoComPendencia) {
                    // Processo não liberado já está na lista de pendencias
                    processoComPendencia.pendencias.push(Configuracao.PENDENCIA_PROCESSO_NAO_LIBERADO);
                } else {
                    // Inserir novo processo
                    let novoProcessoPendente = {
                        numeroProcesso: processoNaoLiberado.processo.numeroCompleto,
                        pendencias: [Configuracao.PENDENCIA_PROCESSO_NAO_LIBERADO]
                    };
                    pendencias.pendenciasProcessos.push(novoProcessoPendente);
                    pendencias.quantidadeProcessosPendentes++;
                }
            }
        });
    }

    isPautaPublicada(registro) {
        let dataPublicacaoPauta =
            this.sessaoStore.sessaoSelecionada.prazosPorSessao[registro.sessaoId].dataPublicacaoPauta;
        return (
            dataPublicacaoPauta &&
            moment(dataPublicacaoPauta, 'DD-MM-YYYY-HH-mm-ss')
                .startOf('day')
                .isSameOrBefore(moment().endOf('day'), 'day')
        );
    }

    isPermitidaAlteracaoTipoJulgamento(registro) {
        const restricoes = this.verificarCondicoesParaAlteracaoMeioJulgamentoSatisfeitas(registro);
        return restricoes.length === 0;
    }

    isPermitidaRemessa(registro) {
        const restricoes = this.verificarCondicoesParaRemessaSatisfeitas(registro);
        return restricoes.length === 0;
    }

    temInscricaoAdvParaMeioJulgamentoAtual(registro) {
        return !!(
            (registro.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo &&
                this.temPartipacaoVirtual(registro)) ||
            (registro.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo &&
                this.temPartipacaoSessao(registro))
        );
    }

    /**
     * Verifica se é possível alterar o meio de julgamento:
     * de "P" para "V" em sessões exclusivamente virtuais;
     * de "V" para "P" em sessões exclusivamente presenciais.
     *
     * Não pode ser feita a alteração nos seguintes casos:
     * sessões vinculadas (sessões presenciais com período virtual), com processo confirmado ou publicado em pauta;
     * sessões encerradas ou fechadas;
     * processos com remessa;
     * processos com inscrição de advogado;
     * processos com acórdão transferido;
     * processos com movimentos enviados.
     *
     * @param {Object} registro O registro com os dados do processo.
     * @returns {Array} Um array com as restrições não atendidas.
     */
    verificarCondicoesParaAlteracaoMeioJulgamentoSatisfeitas(registro) {
        const restricoes = [];
        const processoForaDaPautaSessao = !registro.sessaoId;

        if (processoForaDaPautaSessao) {
            restricoes.push('Alteração permitida somente para processos em sessão');
            return restricoes;
        }

        const processoConfirmadoOuPublicado = this.isProcessoConfirmadoEmPautaSessao(registro);

        if (!processoConfirmadoOuPublicado) {
            return restricoes;
        }

        const julgamentoExclusivamenteVirtual =
            SituacaoJulgamentoProcessoBusiness.isProcessoPautadoEmJulgamentoExclusivamenteVirtual(
                this.sessaoStore?.sessaoSelecionada?.tipoJulgamento
            );

        if (registro.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo && julgamentoExclusivamenteVirtual) {
            const msg = 'O meio de julgamento VIRTUAL é o único possível para uma sessão exclusivamente virtual';
            restricoes.push(msg);
        }

        const julgamentoExclusivamentePresencial =
            SituacaoJulgamentoProcessoBusiness.isJulgamentoExclusivamentePresencial(
                this.sessaoStore?.sessaoSelecionada
            );

        if (
            registro.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo &&
            julgamentoExclusivamentePresencial
        ) {
            const msg = 'O meio de julgamento PRESENCIAL é o único possível para uma sessão exclusivamente presencial';
            restricoes.push(msg);
        }

        if (!julgamentoExclusivamenteVirtual && !julgamentoExclusivamentePresencial && processoConfirmadoOuPublicado) {
            const msg = 'Processo confirmado e/ou publicado em sessão presencial vinculada a julgamento virtual';
            restricoes.push(msg);
        }

        if (this.isSessaoFinalizada) {
            const msg = 'Sessão finalizada';
            restricoes.push(msg);
        }

        const temRemessa = SituacaoJulgamentoProcessoBusiness.isProcessoRemetido(
            registro.tipoJulgamento,
            registro.remessaJulgamento
        );

        if (temRemessa) {
            const msg = 'Processo remetido';
            restricoes.push(msg);
        }

        const temInscricaoAdv = this.temInscricaoAdvParaMeioJulgamentoAtual(registro);

        if (temInscricaoAdv) {
            const msg = 'Processo com inscrição de advogado para o meio de julgamento atual';
            restricoes.push(msg);
        }

        if (registro.acordaoTransferido) {
            const msg = 'Processo com acórdão transferido';
            restricoes.push(msg);
        }

        if (registro.processo.origem === Configuracao.ORIGEM_PJE && registro.movimentosTransferidos) {
            const msg = 'Processo com movimentos transferidos';
            restricoes.push(msg);
        }

        return restricoes;
    }

    verificarCondicoesParaRemessaSatisfeitas(registro) {
        const restricoes = [];

        if (this.isSessaoFinalizada) {
            const msg = 'Sessão finalizada';
            restricoes.push(msg);
        }

        const temRemessa = SituacaoJulgamentoProcessoBusiness.isProcessoRemetido(
            registro.tipoJulgamento,
            registro.remessaJulgamento
        );

        if (temRemessa) {
            const msg = 'Processo já remetido';
            restricoes.push(msg);
        }

        if (registro.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo) {
            const msg = 'O meio de julgamento PRESENCIAL não permite remessa';
            restricoes.push(msg);
        }

        return restricoes;
    }

    get isSessaoFinalizada() {
        if (this.sessaoStore.sessaoSelecionada?.prazos?.dataFimVotacaoVirtual) {
            const dataEncerramentoSessaoObj = dayjs(
                this.sessaoStore.sessaoSelecionada.prazos.dataFimVotacaoVirtual,
                Configuracao.FORMATO_DATAHORA_SERVICOS_PADRAO
            );

            return dataEncerramentoSessaoObj.isBefore(dayjs());
        } else if (this.sessaoStore.sessaoSelecionada?.prazos?.dataInicioSessaoPresencial) {
            const dataEncerramentoSessaoObj = dayjs(
                this.sessaoStore.sessaoSelecionada.prazos.dataInicioSessaoPresencial,
                Configuracao.FORMATO_DATAHORA_SERVICOS_PADRAO
            );

            return dataEncerramentoSessaoObj.isBefore(dayjs());
        }

        return false;
    }

    isProcessoConfirmadoEmPautaSessao(registro) {
        return (
            registro.confirmadaInclusaoPauta ||
            (registro.materiaPublicacaoPauta &&
                registro.materiaPublicacaoPauta.situacao !== SITUACAO_PUBLICACAO_PAUTA.CANCELADO.id &&
                registro.materiaPublicacaoPauta.situacao !== SITUACAO_PUBLICACAO_PAUTA.CANCELADO_PARCIALMENTE.id)
        );
    }

    podeAlterarTipoJulgamentoSemNecessidadeRemeter(registro) {
        const semInscricaoAdv =
            !registro?.pedidoSustentacaoOral?.pedidosDetalhados ||
            registro.pedidoSustentacaoOral.pedidosDetalhados.length === 0;

        return semInscricaoAdv && !this.isProcessoApreciado(registro);
    }

    /**
     * Busca as inscrições de advogados não marcados como ausentes para processos não apreciados.
     *
     * @param {string | undefined} [siglaRelator] A sigla do relator, caso seja aplicado o filtro "por relator".
     * @param {boolean} [verificarRegrasPregaoPlanilha] Um sinalizador que indica se devem ser incluídos apenas os processos que podem ser apregoados em lote (na planilha).
     * @returns {Object[]} A lista de inscrições que correspondem aos critérios de busca.
     */
    findInscricoesAdvogadosNaoAusentesDeProcessosNaoApreciados(
        siglaRelator = undefined,
        verificarRegrasPregaoPlanilha = true
    ) {
        const inscricoes = [];

        this.processosSessao.forEach((proc) => {
            if (
                (!siglaRelator || siglaRelator === proc.processo.relator.sigla) &&
                !this.isProcessoApreciado(proc) &&
                (!verificarRegrasPregaoPlanilha || this.podeApregoarNaPlanilha(proc)) &&
                proc.pedidoSustentacaoOral?.pedidosDetalhados
            ) {
                proc.pedidoSustentacaoOral.pedidosDetalhados.forEach((pedido) => {
                    if (pedido.advogados) {
                        pedido.advogados.forEach((advogado) => {
                            if (
                                advogado.modoParticipacao !== MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo &&
                                advogado.presente !== SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo
                            ) {
                                const dto = {};
                                dto.advogado = advogado;
                                dto.codigoDaParte = pedido.codigoDaParte;
                                dto.denominacaoDaParte = pedido.denominacaoDaParte;
                                dto.nomeDaParte = pedido.nomeDaParte;

                                const inscricao = {
                                    sessaoId: proc.sessaoId,
                                    numeroProcesso: proc.processo.numeroCompleto,
                                    inscricao: dto
                                };

                                inscricoes.push(inscricao);
                            }
                        });
                    }
                });
            }
        });

        return inscricoes;
    }

    marcarAdvogadosAusentesLocalParaProcessosApregoaveisEmPlanilha(
        siglaRelator,
        apenasParaProcessosNaoApreciados = true
    ) {
        if (!this.sessaoStore?.sessaoSelecionada?.processos) {
            return;
        }

        this.sessaoStore.sessaoSelecionada.processos.forEach((proc) => {
            if (
                (!siglaRelator || siglaRelator === proc.processo.relator.sigla) &&
                (!apenasParaProcessosNaoApreciados || !this.isProcessoApreciado(proc)) &&
                this.podeApregoarNaPlanilha(proc) &&
                proc.pedidoSustentacaoOral?.pedidosDetalhados
            ) {
                proc.pedidoSustentacaoOral.pedidosDetalhados.forEach((pedido) => {
                    if (pedido.advogados) {
                        pedido.advogados.forEach((advogado) => {
                            if (
                                advogado.modoParticipacao !== MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo &&
                                advogado.presente !== SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo
                            ) {
                                runInAction(() => {
                                    advogado.presente = SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo;
                                });
                            }
                        });
                    }
                });
            }
        });
    }

    existeInscricao(inscricao, inscricoes) {
        const indice = inscricoes.findIndex((insc) => {
            const achou =
                insc.sessaoId === inscricao.sessaoId &&
                insc.numeroProcesso === inscricao.numeroProcesso &&
                insc.inscricao?.codigoDaParte === inscricao.inscricao?.codigoDaParte &&
                insc.inscricao?.denominacaoDaParte === inscricao.inscricao?.denominacaoDaParte &&
                insc.inscricao?.nomeDaParte === inscricao.inscricao?.nomeDaParte &&
                insc.inscricao?.advogado?.regOAB &&
                inscricao.inscricao?.advogado?.regOAB &&
                insc.inscricao?.advogado.regOAB.numero === inscricao.inscricao.advogado.regOAB.numero &&
                ((!insc.inscricao?.advogado.regOAB.letra && !inscricao.inscricao.advogado.regOAB.letra) ||
                    insc.inscricao?.advogado.regOAB.letra === inscricao.inscricao.advogado.regOAB.letra) &&
                insc.inscricao?.advogado.regOAB?.siglaUf === inscricao.inscricao?.advogado?.regOAB?.siglaUf;

            return achou;
        });

        return indice !== -1;
    }

    async alterarPresencaAdvogado(inscricao, notificarClientes) {
        let resultadoProcessamento;
        let descricaoRegistroProcessado;

        try {
            if (!inscricao) {
                throw new Error('Dados da inscrição ausentes.');
            }

            descricaoRegistroProcessado = `${inscricao.numeroProcesso} - Adv. ${this.getRepresentanteKey(inscricao.inscricao.advogado).replaceAll('|', ' - ')}`;

            resultadoProcessamento = await this.processoService.registrarPresencaAdvogadoProcesso(
                inscricao.sessaoId,
                inscricao.numeroProcesso,
                inscricao.inscricao,
                notificarClientes
            );

            if (resultadoProcessamento && !ErrorUtil.isResultadoErro(resultadoProcessamento)) {
                resultadoProcessamento.numeroProcesso = descricaoRegistroProcessado;
                runInAction(() => {
                    console.log('[FIXME] Alterando a presença do advogado...');
                });
            }
        } catch (error) {
            resultadoProcessamento = ErrorUtil.getResultadoProcessamentoErro(error, descricaoRegistroProcessado);
        }

        this.incrementarProgressoLote();

        return resultadoProcessamento;
    }

    marcarAdvogadosAusentes(
        inscricoes,
        apenasParaProcessosNaoApreciados = true,
        persistirServidor = true,
        // eslint-disable-next-line no-unused-vars
        notificarClientes = true
    ) {
        const inscricoesParaAlterar = [];

        this.processosSessao.forEach((proc) => {
            if (
                (!apenasParaProcessosNaoApreciados || !this.isProcessoApreciado(proc)) &&
                proc.pedidoSustentacaoOral?.pedidosDetalhados
            ) {
                proc.pedidoSustentacaoOral.pedidosDetalhados.forEach((pedido) => {
                    if (pedido.advogados) {
                        pedido.advogados.forEach((advogado) => {
                            if (advogado.modoParticipacao !== MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo) {
                                if (!persistirServidor) {
                                    runInAction(() => {
                                        advogado.presente = SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo;
                                    });
                                } else {
                                    const dto = {};
                                    dto.advogado = toJS(advogado);
                                    dto.codigoDaParte = pedido.codigoDaParte;
                                    dto.denominacaoDaParte = pedido.denominacaoDaParte;
                                    dto.nomeDaParte = pedido.nomeDaParte;

                                    const inscricao = {
                                        sessaoId: proc.sessaoId,
                                        numeroProcesso: proc.processo.numeroCompleto,
                                        inscricao: dto
                                    };

                                    // Altera só a presença: usa o PATCH dedicado (in-place, ID estável) por inscrição.
                                    if (this.existeInscricao(inscricao, inscricoes) && advogado.idInscricao) {
                                        inscricoesParaAlterar.push({
                                            idInscricao: advogado.idInscricao,
                                            descricao: `${proc.processo.numeroCompleto} - Adv. ${this.getRepresentanteKey(advogado).replaceAll('|', ' - ')}`
                                        });
                                    }
                                }
                            }
                        });
                    }
                });
            }
        });

        if (persistirServidor) {
            this.processarLote({
                titulo: 'Marcando ausências de advogados para processos não apreciados',
                qtdTotalProcessos: inscricoesParaAlterar.length,
                lotesProcessos: inscricoesParaAlterar,
                indeterminate: false,
                ocultarProcessamentoLote: true,
                mensagemSucesso: 'Ausências de advogados marcadas com sucesso.',
                funcaoOperacao: (item) => this.marcarPresencaAusenteInscricao(item)
            });
        }
    }

    async marcarPresencaAusenteInscricao(item) {
        let resultadoProcessamento;

        try {
            const resposta = await this.advogadoSustentacaoOralService.alterarPresencaInscricao(
                item.idInscricao,
                SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo
            );

            if (resposta === true) {
                resultadoProcessamento = { numeroProcesso: item.descricao, tipo: 'SUCESSO' };
            } else {
                throw new Error(typeof resposta === 'string' ? resposta : 'Falha ao marcar ausência do advogado.');
            }
        } catch (error) {
            resultadoProcessamento = ErrorUtil.getResultadoProcessamentoErro(error, item.descricao);
        }

        this.incrementarProgressoLote();

        return resultadoProcessamento;
    }

    marcarAdvogadosAusentesEmLoteUmAUm(
        inscricoes,
        apenasParaProcessosNaoApreciados = true,
        persistirServidor = true,
        notificarClientes = true
    ) {
        const inscricoesParaAlterar = [];

        this.processosSessao.forEach((proc) => {
            if (
                (!apenasParaProcessosNaoApreciados || !this.isProcessoApreciado(proc)) &&
                proc.pedidoSustentacaoOral?.pedidosDetalhados
            ) {
                proc.pedidoSustentacaoOral.pedidosDetalhados.forEach((pedido) => {
                    if (pedido.advogados) {
                        pedido.advogados.forEach((advogado) => {
                            if (advogado.modoParticipacao !== MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo) {
                                if (!persistirServidor) {
                                    runInAction(() => {
                                        advogado.presente = SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo;
                                    });
                                } else {
                                    const dto = {};
                                    dto.advogado = toJS(advogado);
                                    dto.codigoDaParte = pedido.codigoDaParte;
                                    dto.denominacaoDaParte = pedido.denominacaoDaParte;
                                    dto.nomeDaParte = pedido.nomeDaParte;

                                    const inscricao = {
                                        sessaoId: proc.sessaoId,
                                        numeroProcesso: proc.processo.numeroCompleto,
                                        inscricao: dto
                                    };

                                    if (this.existeInscricao(inscricao, inscricoes)) {
                                        inscricao.inscricao.advogado.presente =
                                            SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo;
                                        inscricoesParaAlterar.push(inscricao);
                                    }
                                }
                            }
                        });
                    }
                });
            }
        });

        if (persistirServidor) {
            const config = {
                titulo: 'Marcando ausências de advogados para processos não apreciados',
                qtdTotalProcessos: inscricoesParaAlterar.length,
                lotesProcessos: inscricoesParaAlterar,
                indeterminate: false,
                ocultarProcessamentoLote: true,
                mensagemSucesso: 'Ausências de advogados marcadas com sucesso.',
                funcaoOperacao: (inscricao) => this.alterarPresencaAdvogado(inscricao, notificarClientes)
            };

            this.processarLote(config);
        }
    }

    get isPermitidaApreciacaoProcessos() {
        return !this.existeSessaoPJe || this.isPermitidaApreciacaoProcessosPJe;
    }

    get isPermitidaApreciacaoProcessosPJe() {
        return this.sessaoStore.isPermitidaApreciacaoProcessosPJe;
    }

    isPermitidaApreciacaoProcesso(registro) {
        return (
            registro &&
            ((registro.processo.origem === Configuracao.ORIGEM_SIJ && !registro.necessitaImportacaoSalaSessao) ||
                (registro.processo.origem === Configuracao.ORIGEM_PJE && this.isPermitidaApreciacaoProcessosPJe))
        );
    }

    get mapaProcessosPartes() {
        return this.sessaoStore.mapaProcessosPartesSessao;
    }

    get mapaMovimentacoesInternas() {
        return this.sessaoStore.mapaMovimentacoesInternas;
    }

    async carregarMovimentosDecisoesSessao() {
        if (!this.carregandoMovimentosDecisoes) {
            try {
                this.carregandoMovimentosDecisoes = true;
                await this.sessaoStore.carregarMovimentosDecisoesSessaoSelecionada();
            } finally {
                runInAction(() => (this.carregandoMovimentosDecisoes = false));
            }
        }
    }

    async recuperarUrlEdicaoVoto(registro) {
        if (!registro.informacoesAdicionais.urlEdicaoVoto) {
            const urlEdicaoVoto = await this.processoService.recuperarUrlEdicaoVoto(registro);
            runInAction(() => (registro.informacoesAdicionais.urlEdicaoVoto = urlEdicaoVoto));
        }
    }

    validarFecharPauta(sessaoId) {
        const processos = this.getProcessosSessao(sessaoId);

        if (processos.length === 0) {
            return { erro: true, mensagem: 'Nenhum processo foi incluído na pauta ' + sessaoId + '.' };
        }

        const naoConfirmadasInclusao = processos
            .filter((processo) => processo.confirmadaInclusaoPauta === false)
            .map((processo) => processo.processo.numeroCompleto);

        if (naoConfirmadasInclusao.length > 0) {
            const mensagem =
                naoConfirmadasInclusao.length === 1
                    ? `O processo ${naoConfirmadasInclusao.join()} não foi confirmado na pauta ${sessaoId}. Confirme este processo em pauta ou exclua-o da pauta.`
                    : `Os processos ${naoConfirmadasInclusao.join()} não foram confirmados na pauta ${sessaoId}. Confirme estes processos em pauta ou exclua-os da pauta.`;

            return { erro: true, mensagem };
        }

        return { erro: false, mensagem: `Deseja fechar a pauta ${sessaoId}?` };
    }
}
